import {
  AlertCircleIcon,
  AtSignIcon,
  Building2Icon,
  CircleMinusIcon,
  createLucideIcon,
  FilterIcon,
  LayersIcon,
  LucideIcon,
  MessageCircleWarningIcon,
  MessageSquareIcon,
  SearchIcon,
  SignalHighIcon,
  SignalLowIcon,
  SignalMediumIcon,
  SmartphoneIcon,
  StarIcon,
  ThumbsUpIcon,
  UsersIcon,
} from "lucide-react";

import {
  AgenticScoreBucket,
  AvailabilityTimeRange,
  BusinessTrend,
  CallStatus,
  CandidacyRejectionReason,
  CandidacyRejectionReasonGroup,
  CandidacySource,
  CandidacyStatus,
  CommunicationFrequency,
  CommunicationMode,
  CompanyIndustry,
  CompanyIntakeTab,
  CompensationType,
  DashboardHeaderColor,
  DateFilterOption,
  DayOfWeek,
  DemoPosition,
  DemoRegion,
  EmploymentType,
  InterviewDuration,
  InterviewQueryParams,
  InterviewRoundType,
  InterviewSort,
  InterviewStatus,
  InterviewType,
  JobBottleneck,
  JobCategory,
  JobDifficulty,
  JobHealth,
  JobIntakeTab,
  JobPauseType,
  JobQueryParams,
  JobsSortKeyAndOrder,
  JobStatus,
  JobTrackerSort,
  JobUrgency,
  LaborupProRank,
  LeaderboardQueryParams,
  LeaderboardSort,
  MyCandidaciesQueryParams,
  MyCandidaciesSortBy,
  NotificationCatalogEntry,
  NotificationCategoryId,
  NotificationChannel,
  NotificationFilterState,
  NotificationInboxFilter,
  NotificationPreferenceGroup,
  NotificationType,
  OvertimeAvailability,
  PaymentTerms,
  PerformanceTier,
  PetYardWeatherCondition,
  PipelineFilterAndSortState,
  PipelineStage,
  PlacementQueryParams,
  PlacementStatus,
  PlacementTrackerSortBy,
  PlayyardSkyPhase,
  PlayyardSkyPreview,
  PortfolioHealthScoreTier,
  PositionCategory,
  Progress,
  PushMode,
  PushType,
  QuoMessageStatus,
  RequisitionQueryParams,
  RequisitionSort,
  RequisitionStatus,
  RevenueType,
  Role,
  SadieDashboardRange,
  ScreeningAvailability,
  ShiftType,
  JobPipelineSortKey,
  SortOrder,
  SourcedType,
  StaffingFirmCostStructure,
  StakeholderRole,
  StakeholderValuePerception,
  TaskPriority,
  TaskQueryParams,
  TaskSort,
  TaskStatus,
  TeamGoalField,
  TextAIOutreachStatus,
  Timezone,
  UserRole,
  VoiceAIOutreachStatus,
  ExternalResumeSource,
} from "@/types";
import type { HexGradient, SearchV2Weights } from "@/types";

// DATA //

export const ALL_USERS_KEY = "all" as const;
export const ALL_USERS_LABEL = "All";

export const UNREAD_NOTIFICATIONS_DISPLAY_CAP = 9;

// Bridgetown production operator job id. The Stevie tab itself is on every job
// now. This only keeps the Submit / Do Not Submit badge, and the Records-tab
// AI Calls hiding, with the pilot until both are signed off for every role.
export const STEVIE_TAB_JOB_ID = "676e8801-c308-4405-802c-dcd6762b2e0d";

// The types behind the sidebar's "Top candidates" row and its sectioned slideout.
export const TOP_CANDIDATE_NOTIFICATION_TYPES = [
  NotificationType.EXCELLENT_CANDIDATE,
  NotificationType.INTERNAL_RECOMMENDATION,
];

// The types behind the sidebar's "Unread mentions" row
export const MENTION_NOTIFICATION_TYPES = [
  NotificationType.INTERNAL_COMMENT_MENTION,
  NotificationType.THREAD_REPLY,
  NotificationType.JOB_COMMENT_MENTION,
  NotificationType.COMPANY_COMMENT_MENTION,
];

export const MATT_ID = 35;
export const TYLER_ID = 36;
export const CORBIN_ID = 7;
export const MICHAEL_ID = 1;
export const SIMBA_ID = 2;
export const ERIKA_ID = 47;
export const CHITRANG_ID = 8;
export const BAILEY_ID = 39;

// Admins. Gates internal-only affordances: the My Aviary board selector,
// assigning job managers, and appending manual commission adjustments.
// Everyone else sees those read-only or not at all.
export const ADMIN_USER_IDS = [
  TYLER_ID,
  MATT_ID,
  MICHAEL_ID,
  ERIKA_ID,
  CHITRANG_ID,
  CORBIN_ID,
  SIMBA_ID,
];

// Leadership can view any staffer's earnings page via the staffer selector.
export const EARNINGS_ADMIN_VIEWER_USER_IDS = [
  SIMBA_ID,
  CORBIN_ID,
  MATT_ID,
  MICHAEL_ID,
  ERIKA_ID,
  TYLER_ID,
];
// Early-access rollout while earnings data accuracy is being verified
export const EARNINGS_PAGE_USER_IDS = [
  MATT_ID,
  MICHAEL_ID,
  ERIKA_ID,
  BAILEY_ID,
];

// Tailwind stroke classes for the dashboard Goals card rings, one per metric.
export const teamGoalRingColorByField: Record<TeamGoalField, string> = {
  [TeamGoalField.SUBMITS]: "stroke-primary-500",
  [TeamGoalField.INTERVIEWS]: "stroke-violet-500",
  [TeamGoalField.OFFERS]: "stroke-amber-500",
  [TeamGoalField.PLACEMENTS]: "stroke-emerald-500",
  [TeamGoalField.REVENUE]: "stroke-teal-500",
};

export const EMPTY_PROGRESS: Progress = { filled: 0, total: 0, percent: 0 };

export const COMMS_STATUSES: CandidacyStatus[] = [
  CandidacyStatus.SHORTLIST,
  CandidacyStatus.OUTREACH,
  CandidacyStatus.FOLLOWINGUP,
  CandidacyStatus.ENGAGED,
];

export const MAX_CONTACTS_ATTEMPTED = 4;

export const RECOMMENDED_JOBS_LIMIT = 5;

export const MS_PER_DAY = 1000 * 60 * 60 * 24;
export const USER_SHEET_DEFAULT_COMMS_WIDTH_PERCENT = 25;
export const MS_PER_SECOND = 1000;
export const MS_PER_MINUTE = 1000 * 60;

// How long the owner has to take a call a candidate asked for before the
// scheduling agent answers instead. Mirrors CALL_REQUEST_WINDOW_MS in guac —
// the banner's countdown has to expire when the backend's window does.
export const CALL_REQUEST_WINDOW_MS = 2 * MS_PER_MINUTE;

// Quick reschedule options for "Screening in Progress" modal
export const SCREEN_RESCHEDULE_QUICK_DELAYS_MINUTES = [20, 40, 60];

// Prompts that float over sheets/modals — the screen card, its portaled
// calendar/time popover, and the notification stack. Clicks inside them must
// not count as "outside" for whatever they float over.
export const FLOATING_PROMPT_SELECTOR =
  "[data-screening-card], [data-notification-popup], .screening-prompt-portal";

// The notification popup and the screening prompt stack in the same corner, so
// both read these instead of sizing to their own content — that's the only way
// the two stay identical as their contents change.
export const CORNER_POPUP_WIDTH_CLASS = "w-[min(100vw-2rem,460px)]";
// Fits the notification popup's tallest safe content: a one-line title, a
// two-line body, and the meta row, inside its p-4.
export const CORNER_POPUP_HEIGHT_CLASS = "h-[112px]";
// Same footprint as a floor rather than a cap, for the notification popup —
// long bodies shrink their type first and grow the card only once that runs out.
export const CORNER_POPUP_MIN_HEIGHT_CLASS = "min-h-[112px]";

// Popups clear themselves after a glance. The two top priority candidate types
// are the exception — they hold the corner until the candidacy is worked.
export const VANISHING_POPUP_DURATION_MS = 15_000;
export const PERSISTENT_POPUP_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.EXCELLENT_CANDIDATE,
  NotificationType.CANDIDATE_RESURRECTED_AFTER_STEVIE,
];

// The screening prompt shares its corner with the notification popups, so it
// holds the same footprint and the same button/label scale on every step.
export const SCREENING_PROMPT_ACTION_CLASSNAME =
  "h-7 min-w-0 flex-1 rounded-full px-2 text-[11px] font-semibold";
export const SCREENING_PROMPT_KICKER_CLASSNAME =
  "text-[10px] font-semibold uppercase tracking-wider text-slate-500";

// An interview in one of these statuses is still "live" — everything else
// (completed, no-show, canceled) is terminal.
export const NON_TERMINAL_INTERVIEW_STATUSES = [
  InterviewStatus.SCHEDULED,
  InterviewStatus.CANDIDATE_CONFIRMED,
];

export const DASHBOARD_HEADER_HEIGHT_PX = 112;
export const DASHBOARD_HEADER_MINIMIZED_HEIGHT_PX = 56;
export const CANDIDATE_SHEET_KICKER_LABEL_CLASSNAME =
  "font-plex-mono text-[11px] font-medium uppercase tracking-wider text-slate-400";
export const DASHBOARD_SCHEDULE_DAYS = 7;

// Fixed height + padding for the three dashboard stat cards (Corin's compact design).
export const DASHBOARD_STAT_CARD_HEIGHT_CLASS = "h-44 max-h-44 min-h-44";
// Pixel twin of DASHBOARD_STAT_CARD_HEIGHT_CLASS (h-44) so the stat-row grid
// track can animate open/closed.
export const DASHBOARD_STAT_ROW_HEIGHT_PX = 176;
export const DASHBOARD_STAT_CARD_PADDING_CLASS = "px-4 pb-4 pt-3.5";
export const DASHBOARD_CALENDAR_EXPANDED_KEY = "dashboard:calendar:expanded";
export const DASHBOARD_HEADER_MINIMIZED_KEY = "dashboard:header:minimized";

// One shared grid spans the stat row + main content so the Top Candidates
// card and the schedule panel land in the same column — pixel-identical
// width by construction, per Corin's design.
export const DASHBOARD_CONTENT_GRID_GAP_PX = 24;
export const DASHBOARD_CONTENT_GRID_TEMPLATE_COLUMNS = `minmax(0, 6fr) ${DASHBOARD_CONTENT_GRID_GAP_PX.toString()}px minmax(0, 3fr) ${DASHBOARD_CONTENT_GRID_GAP_PX.toString()}px minmax(0, 3fr)`;
export const DASHBOARD_GRID_GOALS_COLUMN_CLASS = "[grid-column:1]";
export const DASHBOARD_GRID_FUNNEL_COLUMN_CLASS = "[grid-column:3]";
export const DASHBOARD_GRID_STATS_COLUMN_CLASS = "[grid-column:5]";
export const DASHBOARD_GRID_TABLE_COLUMN_CLASS = "[grid-column:1/4]";
export const DASHBOARD_GRID_FAB_COLUMN_CLASS = "[grid-column:4]";
export const DASHBOARD_GRID_SCHEDULE_COLUMN_CLASS = "[grid-column:5]";
export const DASHBOARD_GRID_FULL_WIDTH_COLUMN_CLASS = "[grid-column:1/-1]";
export const DASHBOARD_SECTION_GAP_PX = 24;

// Dashboard header color picker — order, labels, banner gradients, swatch gradients.
export const DASHBOARD_HEADER_COLOR_ORDER: DashboardHeaderColor[] = [
  DashboardHeaderColor.LABORUP_ORIGINAL,
  DashboardHeaderColor.NAVY,
  DashboardHeaderColor.SLATE,
  DashboardHeaderColor.FOREST,
  DashboardHeaderColor.DUSTY_PINK,
  DashboardHeaderColor.SKY,
  DashboardHeaderColor.CORAL,
  DashboardHeaderColor.MINT,
  DashboardHeaderColor.LILAC,
  DashboardHeaderColor.PLUM,
  DashboardHeaderColor.TEAL,
];

export const dashboardHeaderColorToLabel: Record<DashboardHeaderColor, string> =
  {
    [DashboardHeaderColor.LABORUP_ORIGINAL]: "Laborup Original",
    [DashboardHeaderColor.NAVY]: "Deep Navy",
    [DashboardHeaderColor.SLATE]: "Cool Slate",
    [DashboardHeaderColor.FOREST]: "Deep Forest",
    [DashboardHeaderColor.DUSTY_PINK]: "Dusty Pink",
    [DashboardHeaderColor.SKY]: "Clear Sky",
    [DashboardHeaderColor.CORAL]: "Warm Coral",
    [DashboardHeaderColor.MINT]: "Fresh Mint",
    [DashboardHeaderColor.LILAC]: "Soft Lilac",
    [DashboardHeaderColor.PLUM]: "Rich Plum",
    [DashboardHeaderColor.TEAL]: "Ocean Teal",
  };

// Laborup Original reuses the existing .bg-dashboard-header CSS gradient.
export const dashboardHeaderColorToBannerClassName: Record<
  DashboardHeaderColor,
  string
> = {
  [DashboardHeaderColor.LABORUP_ORIGINAL]:
    "bg-dashboard-header dark:bg-[linear-gradient(115deg,#1E3145_0%,#27435E_48%,#355477_100%)]",
  [DashboardHeaderColor.NAVY]:
    "bg-[linear-gradient(115deg,#204468_0%,#143252_48%,#12304a_100%)]",
  [DashboardHeaderColor.SLATE]:
    "bg-[linear-gradient(115deg,#505e6e_0%,#364252_48%,#2e3844_100%)]",
  [DashboardHeaderColor.FOREST]:
    "bg-[linear-gradient(115deg,#285450_0%,#1a4038_48%,#18342e_100%)]",
  [DashboardHeaderColor.DUSTY_PINK]:
    "bg-[linear-gradient(115deg,#967880_0%,#725660_48%,#624850_100%)]",
  [DashboardHeaderColor.SKY]:
    "bg-[linear-gradient(115deg,#587488_0%,#3e586c_48%,#344a58_100%)]",
  [DashboardHeaderColor.CORAL]:
    "bg-[linear-gradient(115deg,#a66c5c_0%,#845048_48%,#76443c_100%)]",
  [DashboardHeaderColor.MINT]:
    "bg-[linear-gradient(115deg,#508080_0%,#3c6060_48%,#345050_100%)]",
  [DashboardHeaderColor.LILAC]:
    "bg-[linear-gradient(115deg,#726890_0%,#584e72_48%,#4e4060_100%)]",
  [DashboardHeaderColor.PLUM]:
    "bg-[linear-gradient(115deg,#544064_0%,#40304a_48%,#382838_100%)]",
  [DashboardHeaderColor.TEAL]:
    "bg-[linear-gradient(115deg,#286060_0%,#1a4848_48%,#183a3a_100%)]",
};

// Slightly brighter variants for the picker swatches.
export const dashboardHeaderColorToSwatchClassName: Record<
  DashboardHeaderColor,
  string
> = {
  [DashboardHeaderColor.LABORUP_ORIGINAL]:
    "bg-[linear-gradient(115deg,#1e3a8a_0%,#2563eb_60%,#3b82f6_100%)]",
  [DashboardHeaderColor.NAVY]:
    "bg-[linear-gradient(115deg,#245070_0%,#183858_48%,#163454_100%)]",
  [DashboardHeaderColor.SLATE]:
    "bg-[linear-gradient(115deg,#5a6878_0%,#445260_48%,#3e4a58_100%)]",
  [DashboardHeaderColor.FOREST]:
    "bg-[linear-gradient(115deg,#306058_0%,#204a40_48%,#204838_100%)]",
  [DashboardHeaderColor.DUSTY_PINK]:
    "bg-[linear-gradient(115deg,#a08088_0%,#886670_48%,#7e5e68_100%)]",
  [DashboardHeaderColor.SKY]:
    "bg-[linear-gradient(115deg,#688898_0%,#506878_48%,#466074_100%)]",
  [DashboardHeaderColor.CORAL]:
    "bg-[linear-gradient(115deg,#b47868_0%,#9a6254_48%,#8e584e_100%)]",
  [DashboardHeaderColor.MINT]:
    "bg-[linear-gradient(115deg,#609090_0%,#487570_48%,#446868_100%)]",
  [DashboardHeaderColor.LILAC]:
    "bg-[linear-gradient(115deg,#8078a0_0%,#685e82_48%,#60567a_100%)]",
  [DashboardHeaderColor.PLUM]:
    "bg-[linear-gradient(115deg,#5e5070_0%,#483858_48%,#483850_100%)]",
  [DashboardHeaderColor.TEAL]:
    "bg-[linear-gradient(115deg,#307070_0%,#205656_48%,#205050_100%)]",
};

// Solid ring tone matching each header gradient, for small circular avatars.
export const dashboardHeaderColorToRingClassName: Record<
  DashboardHeaderColor,
  string
> = {
  [DashboardHeaderColor.LABORUP_ORIGINAL]: "ring-[#2563eb]",
  [DashboardHeaderColor.NAVY]: "ring-[#204468]",
  [DashboardHeaderColor.SLATE]: "ring-[#505e6e]",
  [DashboardHeaderColor.FOREST]: "ring-[#285450]",
  [DashboardHeaderColor.DUSTY_PINK]: "ring-[#967880]",
  [DashboardHeaderColor.SKY]: "ring-[#587488]",
  [DashboardHeaderColor.CORAL]: "ring-[#a66c5c]",
  [DashboardHeaderColor.MINT]: "ring-[#508080]",
  [DashboardHeaderColor.LILAC]: "ring-[#726890]",
  [DashboardHeaderColor.PLUM]: "ring-[#544064]",
  [DashboardHeaderColor.TEAL]: "ring-[#286060]",
};

// Mon → Sun render order. Single source for iteration; keys double as labels.
export const DAYS_OF_WEEK_ORDERED: readonly DayOfWeek[] = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// 15-min screen + 5-min break between screens. Used by capacity math.
export const SCREEN_LENGTH_MINUTES = 15;
export const SCREEN_BREAK_MINUTES = 5;

export const DEFAULT_AVAILABILITY_RANGE: AvailabilityTimeRange = {
  startTime: "13:00",
  endTime: "18:00",
};

export const EMPTY_SCREENING_AVAILABILITY: ScreeningAvailability = {
  Monday: [],
  Tuesday: [],
  Wednesday: [],
  Thursday: [],
  Friday: [],
  Saturday: [],
  Sunday: [],
};

export const shortTimezoneLabel: Record<Timezone, string> = {
  [Timezone.EASTERN]: "ET",
  [Timezone.CENTRAL]: "CT",
  [Timezone.MOUNTAIN]: "MT",
  [Timezone.MOUNTAIN_ARIZONA]: "MST",
  [Timezone.PACIFIC]: "PT",
};

const demoChattanoogaCompanies = [
  "Volkswagen Chattanooga Assembly Plant",
  "Covenant Transport",
  "McKee Foods Corporation",
  "Astec Industries",
  "Whirlpool Corp",
  "DENSO Mfg. Athens Tennessee",
  "Pilgrim's Pride",
  "Wacker polysilicon",
  "Southern Champion Tray, LP",
  "Mars Chocolate North America",
  "Chattem Inc. (Sanofi)",
  "Mueller Co. (Mueller Water Products)",
  "Nokian Tyres",
  "Formulated Solutions",
  "3M Chattanooga",
  "Komatsu America Corp",
  "Lodge Manufacturing Company",
  "Duracell",
  "Olin",
  "Newly Weds Foods (Cleveland)",
  "Rubbermaid (Newell Brands)",
  "Woodbridge (Chattanooga)",
  "Kordsa, Inc.",
  "LJT Tennessee (Steel Warehouse)",
  "Tennessee Rand, Inc.",
  "BASF Corporation (Chattanooga)",
  "Alstom",
  "Ply Gem (Cornerstone Building Brands)",
  "Card-Monroe Corp.",
  "Koch Foods Inc.",
  "United States Stove Company",
  "Aeroflex USA, Inc.",
  "Roper Corp",
  "La-Z-Boy",
  "Jackson Furniture",
  "TenCate Grass (Dayton)",
  "Baltimore Aircoil Co.",
  "Coca-Cola Bottling",
  "Georgia-Pacific Corp",
  "Toyo Seat USA Corp.",
  "Robinson Manufacturing Co.",
  "Johns Manville",
  "Adient US LLC",
  "Creative Foam Corporation",
  "Heil Trailer International Co",
  "Polartec",
  "Mars Wrigley Confectionery",
  "Mueller Company",
  "Solenis",
  "Cormetech Inc",
  "Ascent Chemicals",
  "ADM Milling",
  "Fun Treats Inc.",
  "SK Food Group",
  "Southeastern Container Inc.",
  "Eaton Corporation",
  "Branch Technology",
  "Flowers Bakery of Cleveland LLC",
  "Miller Industries",
  "Benchmark Tool & Machine",
  "Avail Enclosure Systems",
  "Buzzi Unicem USA (Signal Mountain)",
  "Moeller Marine Products",
  "General Shale (Spring City)",
  "SSM Industries, Inc.",
  "WestRock (RockTenn) Chattanooga",
  "The Chattanooga Bakery",
  "Colonial Chemical, Inc.",
  "Chromalox",
  "Cemex USA",
  "Crown Cork and Seal",
  "CMC",
  "Vulcan Materials",
  "AdTech Ceramics",
  "Siskin Steel",
  "Dunlap Industries, Inc.",
  "Metco Metal Fab",
  "Gestamp Chattanooga",
  "Silvey Metalworks Inc.",
  "Stock'd Supply",
  "Cherokee Steel LLC",
];

const demoCincinnatiCompanies = [
  "ADAM / Wild Flavors (ADM)",
  "AtriCure, Inc.",
  "Bosch Automotive Steering",
  "Can-Do National Tape",
  "Centrus Energy Corp. (American Centrifuge Plant)",
  "Cincinnati Tool Steel Company",
  "Cintas Corporation",
  "Clippard Instrument Laboratory, Inc.",
  "Crown Cork and Seal",
  "Cummins Seymour Engine Plant",
  "Dayton Manufacturing Company",
  "Dayton-Phoenix Group",
  "Eaton (Aerospace Group)",
  "Esco Corp.",
  "Essity",
  "G&J Pepsi-Cola Bottlers Inc.",
  "General Electric (GE) Aerospace",
  "Green Tokai Co.",
  "Heidelberg Materials (Lehigh Cement)",
  "Hixson Architecture Engineering",
  "Honda of America, Anna Engine Plant",
  "Huhtamaki",
  "Hydro Systems Company",
  "J.T.M. Food Group",
  "Jergens, Inc. (Workholding)",
  "King Bag & Manufacturing Company",
  "L'Oréal USA",
  "LION (Safety Equipment)",
  "Linde Gas & Equipment Inc.",
  "Mazak Corporation",
  "Michelin North America",
  "Michelman, Inc.",
  "Midmark Corp.",
  "Navistar",
  "Nestle-Purina PetFood",
  "Panelmatic Cincinnati, Inc.",
  "PolyVision Corporation",
  "Pro Mach, Inc.",
  "Procter & Gamble",
  "R.A JONES (Packaging Machinery)",
  "Senco Brands Inc.",
  "Sun Chemical (A DIC Company)",
  "The Drees Company",
  "The Gorilla Glue Company",
  "The Kroger Co. (Dairy/Bread Plants)",
  "Toyota Motor Manufacturing, Kentucky (TMMK)",
  "Valvoline (HQ & Manufacturing)",
];

const demoClarksvilleCompanies = [
  "Trane Company",
  "Grupo Antolin Kentucky",
  "Jostens",
  "Bridgestone Metalpha",
  "T.RAD North America",
  "Metalsa",
  "LG Electronics",
  "TG Automotive Sealing (Toyoda Gosei)",
  "Dart Container Corporation",
  "Henkel (formerly Sun Products)",
  "Martinrea",
  "General Motors Corvette Assembly",
  "Bowling Green Metalforming (Magna)",
  "Hankook Tire",
  "Trace Die Cast Inc.",
  "Country Oven Bakery",
  "Logan Aluminum Inc.",
  "Kobe Aluminum Automotive Products (KAAP)",
  "Akebono Brake (Glasgow)",
  "Franklin Precision Industry (FPI)",
  "Fruit of the Loom",
  "Holley Performance Products",
  "Houchens Industries",
  "Metalsa Structural Products",
  "Bowling Green Metalforming, LLC (Magna)",
  "Dart Container Corp. Of Kentucky",
  "Commonwealth Aluminum",
  "Logan Aluminum",
  "Trane Technologies",
  "Henkel AG & Co. (formerly Sun Products)",
  "Dana Incorporated",
  "Sumitomo Electric Wiring Systems",
  "Federal-Mogul",
  "A. O. Smith Water Products",
  "Grupo Antolin",
  "Martinrea Heavy Stamping",
  "GM Corvette Bowling Green Assembly",
  "Hankook Tire Tennessee",
  "Eaton Corporation",
  "Cumberland Packaging",
  "Federal Mogul",
  "Magna Seating",
  "Miwon Commercial",
  "Accuride Corporation",
  "Akebono Brake",
  "Toyotetsu Tennessee (TTTN)",
  "Hitachi Astemo",
  "Clarksville Foundry",
  "Flex N Gate",
  "Altra Federal Credit Union Operations",
  "KY Wire",
  "Toyotetsu America",
  "Kobelco Aluminum Automotive Products",
  "Smithfield Manufacturing",
  "Beretta Defense Technologies",
  "Berry Global",
  "Acme Boot Company",
  "Camping World Manufacturing",
  "Clarksville Concrete Company",
  "Unipress USA",
  "Google Data Center",
];

const demoHuntsvilleCompanies = [
  "Mazda Toyota Manufacturing USA, Inc.",
  "Toyota Motor Manufacturing Alabama",
  "Polaris Inc. (Huntsville Plant)",
  "Blue Origin (Huntsville Engine Factory)",
  "Sanmina-SCI Corporation",
  "Pilgrim's Pride (Guntersville Plant)",
  "Kohler Co.",
  "Frito-Lay, Inc.",
  "Tyson Foods (Albertville Complex)",
  "AlaTrade Foods",
  "Maples Industries, Inc.",
  "Marelli (Magneti Marelli) - Pulaski",
  "Nissan North America (Decherd Powertrain Plant)",
  "Tepro, Inc.",
  "Heil Environmental",
  "Koch Foods, Inc. (Collinsville Plant)",
  "Renfro Corporation",
  "TS Tech Alabama",
  "Wayne Farms (Decatur Poultry Plants)",
  "GE Appliances (Haier) - Decatur",
  "3M Company (Decatur)",
  "Nucor Steel Decatur, LLC",
  "United Launch Alliance (ULA)",
  "Gemstone Foods, LLC",
  "Daikin America, Inc.",
  "Indorama Ventures (Decatur)",
  "Steelcase (Athens Plant)",
  "Vuteq (VTNA) Alabama",
  "YKTA (Y-tec Keylex Toyotetsu Alabama)",
  "Federal-Mogul (Tenneco) Athens",
  "Bocar US, Inc.",
  "Mueller Company",
  "WestRock (Stevenson Mill)",
  "HTPG (Heat Transfer Products Group)",
  "Lozier Corporation",
  "Lockheed Martin (Courtland Facility)",
  "Topre America Corporation",
  "REHAU Automotive, LLC",
];

const demoKnoxvilleCompanies = [
  "MAG USA",
  "Aisin Automotive",
  "Eagle Bend Manufacturing",
  "Dura-Line",
  "3M Company",
  "Carlstar Group",
  "Techmer PM",
  "Oak Ridge-Tool Engineering",
  "Centrus Energy",
  "Acraloc Corp",
  "Tennessee Tool & Engineering",
  "Protomet Corporation",
  "Y-12 National Security",
  "CoorsTek",
  "Cherokee Millwright",
  "Boatmate Trailers",
  "ICC International",
  "Newell Brands",
  "Denso",
  "Flex-N-Gate",
  "Arconic",
  "Signet Plastics",
  "Allegiant Manufacturing",
  "DeRoyal",
  "Sonoco Products Co.",
  "SumiRiko",
  "Colgate",
  "Cummins",
  "Howmet Aerospace",
  "Tuff Torq Corporation",
  "Inteplast",
  "Colortech Inc",
  "Oshkosh Manufacturing",
  "Sea Ray Boats",
  "Swiss Technologies, Inc",
  "Magnum Venus Products",
  "Beehive Industries",
  "Bush Brothers & Company",
  "Malibu Boats",
  "Kimberly-Clark Corp",
  "IronCraft",
  "Smith & Wesson",
  "Monterey Mushrooms",
  "Viskase Corporation",
  "Mastercraft",
  "Carlex Glass Company",
  "Yamaha",
  "JTEKT Automotive",
  "SSW Technologies",
  "Precision Technologies",
  "SpaceX",
  "Rivian",
  "Caterpillar",
  "Volkswagen",
  "Blue Origin",
  "Toyota",
  "Tesla",
  "Northrop Grumman",
  "Ford",
  "Nissan",
  "BMW",
  "Bridgestone",
  "Boeing",
];

const demoNashvilleCompanies = [
  "Nissan North America, Inc. (Assembly)",
  "Bridgestone Americas",
  "General Motors (GM)",
  "Electrolux Major Appliances",
  "Taylor Farms Tennessee",
  "Bridgestone (Truck/Bus Tire)",
  "Ultium Cells (GM–LG JV Battery Plant)",
  "Calsonic Kansei North America (Marelli)",
  "Nissan North America (Powertrain)",
  "General Motors Corvette Assembly",
  "A.O. Smith Water Products",
  "General Mills",
  "Adient (Johnson Controls Seating)",
  "Tyson Foods (Shelbyville Complex)",
  "LG Electronics USA (Appliance Plant)",
  "Nissan North America (HQ/R&D)",
  "CoreCivic (HQ)",
  "Jostens (Clarksville/Shelbyville)",
  "National Pen Company",
  "Hankook Tire Manufacturing",
  "Unipres USA, Inc.",
  "Mars Petcare (Mfg/Royal Canin)",
  "JASPER Engines & Transmissions",
  "Tennsco Corporation",
  "Viam Manufacturing",
  "Bath Fitter (Plant)",
  "Batesville Casket Company",
  "NWI Aerostructures",
  "Marelli (formerly Calsonic Kansei)",
  "North American Stamping Group (NASG)",
  "Real Granito Pvt. Ltd.",
  "Lozier Corporation",
  "Delek US Holdings",
  "ABC Technologies",
  "Nemak USA (Dickson Plant)",
  "Milliken & Company",
  "Beretta USA",
  "Newell Brands (Sanford)",
  "Harman International",
  "Sinomax USA",
  "Toyo Automotive Parts",
  "ABB Electrification",
  "YAPP USA Automotive Systems",
  "Quad/Graphics",
  "Schwan Cosmetics USA",
  "Permobil (North American HQ & Plant)",
  "Envision AESC (Smyrna Battery Plant)",
  "Feintool Tennessee",
  "Schneider Electric",
  "Unarco Material Handling",
  "Great Lakes Cheese",
  "Topre America Corporation",
  "Ashley Furniture",
  "Leviton Manufacturing Co.",
  "IB-Tech (Ihara)",
  "Rich Products Corporation",
  "Nifco America",
  "Florim USA (Milestone Tiles)",
  "Ingram Content Group",
  "Bemis Manufacturing",
  "Steel Technologies",
  "WireMasters, Inc.",
  "Ravago Manufacturing",
  "Gibson Brands Inc.",
  "Flowserve",
  "UniFirst",
  "Benjamin Moore",
  "Vestis (Uniform Services)",
  "Oshkosh Vocational Tennessee",
  "DFA/Purity Dairy",
  "Aladdin Temp-Rite",
  "Middle Tennessee Lumber",
  "Century Mold Co., Inc.",
  "Numatics (Emerson)",
  "Lewis Brothers Bakeries",
  "Mac Papers + Packaging",
  "Siemens Industry, Inc.",
  "Sonoco",
  "3M Co.",
  "Colbert Manufacturing",
  "WIRTGEN AMERICA, Inc.",
  "Smelter Service Corporation",
  "Vulcan Materials",
  "Crown Packaging",
  "Carlex (Automotive Glass)",
  "Atlas Copco",
  "Pleasant View Manufacturing Co",
  "Corsicana Bedding",
];

export const demoBirminghamCompanies = [
  "Alabama Plate Cutting Company",
  "Altec Inc.",
  "American Cast Iron Pipe Company (ACIPCO)",
  "Birmingham Fastener Manufacturing",
  "Brose Tuscaloosa",
  "Buffalo Rock Company",
  "CMC Steel Alabama",
  "Honda Manufacturing of Alabama",
  "Kamtek, Inc. (Magna)",
  "Lear Corporation",
  "McWane, Inc.",
  "Mercedes-Benz U.S. International",
  "Nucor Steel Birmingham, Inc.",
  "O'Neal Manufacturing Services",
  "SMP Automotive Systems Alabama",
  "Thompson Tractor Company",
  "U.S. Steel Fairfield Works",
  "Vulcan Materials Company",
  "Vulcan Threaded Products",
  "ZF Chassis Systems",
];

export const demoBowlingGreenCompanies = [
  "ALPLA",
  "Akebono Brake",
  "Bendix Spicer Foundation Brake",
  "Bowling Green Machine & Welding",
  "Bowling Green Metalforming (Magna)",
  "Cannon Automotive Solutions",
  "Country Oven Bakery (Houchens)",
  "Franklin Precision Industry",
  "GM Corvette Assembly Plant",
  "Graham Packaging",
  "Halton Company",
  "Henkel",
  "Hill's Pet Nutrition",
  "Holley Performance Products",
  "K&L Machine, LLC",
  "Kobe Aluminum Automotive Products",
  "Logan Aluminum Inc.",
  "Trace Die Cast Inc.",
];

export const demoClevelandCompanies = [
  "A.J. Rose Manufacturing",
  "Avery Dennison",
  "Cleveland-Cliffs (Cleveland Works)",
  "Copperweld",
  "Eaton",
  "Ford Cleveland Engine Plant",
  "GM Parma Metal Center",
  "Hyster-Yale Group",
  "Kiefer Tool & Mold",
  "Lincoln Electric",
  "Lubrizol",
  "Nordson Corporation",
  "Park Ohio Holdings",
  "Parker Hannifin",
  "Swagelok",
  "Tendon Manufacturing",
];

export const demoColumbusCompanies = [
  "Anheuser-Busch",
  "Anomatic",
  "Columbus Machine Works",
  "Commercial Vehicle Group (CVG)",
  "DuPont",
  "Honda (East Liberty Auto Plant)",
  "Honda (Marysville Auto Plant)",
  "Mettler-Toledo",
  "National Electric Coil",
  "Owens Corning",
  "Rogue Fitness",
  "Sofidel America",
  "Stanley Electric U.S.",
  "The Wasserstrom Company",
  "Worthington Enterprises",
  "Worthington Steel",
];

export const demoDaytonCompanies = [
  "Cargill",
  "Clopay Corporation",
  "Collins Aerospace",
  "Crown Equipment Corporation",
  "Emerson / Copeland",
  "Enjet Aero",
  "F&P America Mfg.",
  "Fuyao Glass America",
  "GE Aerospace",
  "Hobart Brothers (ITW)",
  "Honda Anna Engine Plant",
  "ITW Food Equipment Group",
  "JBK Manufacturing",
  "Lincotek Medical",
  "Millat Industries",
  "Norwood Medical",
  "Parker Hannifin",
  "Stolle Machinery",
];

export const demoLexingtonCompanies = [
  "Aichi Forge USA",
  "Big Ass Fans",
  "Buffalo Trace Distillery",
  "CLARK Material Handling",
  "EnerSys",
  "Hitachi Astemo Americas",
  "Leggett & Platt",
  "Lexair Inc.",
  "Lexmark International",
  "Link-Belt Cranes",
  "Montaplast of North America",
  "Sargent & Greenleaf",
  "Sekisui S-Lec America",
  "Sheridan KY (CJK Group)",
  "Sherwin-Williams",
  "Topy America",
  "Toyota Motor Manufacturing Kentucky (TMMK)",
  "Webasto",
];

export const demoLosAngelesCompanies = [
  "AMRO Fabricating",
  "Al Larson Boat Shop",
  "Anduril",
  "Apex",
  "Arc Boat Company",
  "Avibank Mfg.",
  "Bender CCP",
  "Boeing",
  "CHAOS Industries",
  "CX2",
  "Castelion",
  "Crane Aerospace & Electronics",
  "Delta Hi-Tech",
  "Dockside Machine & Ship Repair",
  "Ducommun",
  "Eaton Aerospace",
  "Epirus",
  "Freeform",
  "GKN Aerospace",
  "Hadrian",
  "Hermeus",
  "Honeywell Aerospace",
  "Howmet Aerospace",
  "Hydraulics International",
  "Impulse Space",
  "K2 Space",
  "Klune Industries",
  "L.A. Gauge Company",
  "L3Harris",
  "Lockheed Martin",
  "M.S. Aerospace",
  "Machina Labs",
  "Millennium Space Systems",
  "Moog",
  "Neros Technologies",
  "Northrop Grumman",
  "Parker Meggitt",
  "Picogrid",
  "Precision Castparts",
  "RTX",
  "Radiant",
  "Relativity Space",
  "Robinson Helicopter Company",
  "Rocket Lab",
  "SpaceX",
  "VACCO Industries",
  "Valar Atomics",
  "Varda Space Industries",
  "Vast",
  "Votaw Precision Technologies",
];

export const demoLouisvilleCompanies = [
  "American Synthetic Rubber Co.",
  "Atlas Machine and Supply",
  "Brown-Forman",
  "Carbide Industries",
  "Derby Welding & Machine Co.",
  "Ford Kentucky Truck Plant",
  "Ford Louisville Assembly Plant",
  "GE Appliances (Appliance Park)",
  "Jewell Manufacturing",
  "Jim Beam (Beam Suntory)",
  "Kentucky Metal Fabrications, Inc.",
  "Kentucky Trailer",
  "Metalsa",
  "O'Neal Manufacturing Services",
  "Sypris Technologies",
  "Zeon Chemicals",
];

export const demoMobileCompanies = [
  "AM/NS Calvert",
  "Airbus Alabama",
  "Alabama Shipyard LLC",
  "Arkema Inc.",
  "Austal USA",
  "BASF Corporation",
  "Borusan Berg Pipe",
  "Continental Aerospace Technologies",
  "Evonik Corporation",
  "Kimberly-Clark (Mobile Mill)",
  "Master Boat Builders",
  "Mobile Pulley Works (SPI)",
  "Olin Corporation",
  "SSAB Americas",
  "ST Engineering Aerospace",
  "Silver Ships, Inc.",
  "Steiner Shipyard, Inc.",
];

export const demoRegionCompanies: Record<
  DemoRegion,
  { label: string; value: string }[]
> = {
  [DemoRegion.BIRMINGHAM_AL]: demoBirminghamCompanies.map((company) => ({
    label: company,
    value: company,
  })),
  [DemoRegion.BOWLING_GREEN_KY]: demoBowlingGreenCompanies.map((company) => ({
    label: company,
    value: company,
  })),
  [DemoRegion.CHATTANOOGA_TN]: demoChattanoogaCompanies.map((company) => ({
    label: company,
    value: company,
  })),
  [DemoRegion.CINCINNATI_OH]: demoCincinnatiCompanies.map((company) => ({
    label: company,
    value: company,
  })),
  [DemoRegion.CLARKSVILLE_TN]: demoClarksvilleCompanies.map((company) => ({
    label: company,
    value: company,
  })),
  [DemoRegion.CLEVELAND_OH]: demoClevelandCompanies.map((company) => ({
    label: company,
    value: company,
  })),
  [DemoRegion.COLUMBUS_OH]: demoColumbusCompanies.map((company) => ({
    label: company,
    value: company,
  })),
  [DemoRegion.DAYTON_OH]: demoDaytonCompanies.map((company) => ({
    label: company,
    value: company,
  })),
  [DemoRegion.HUNTSVILLE_AL]: demoHuntsvilleCompanies.map((company) => ({
    label: company,
    value: company,
  })),
  [DemoRegion.KNOXVILLE_TN]: demoKnoxvilleCompanies.map((company) => ({
    label: company,
    value: company,
  })),
  [DemoRegion.LEXINGTON_KY]: demoLexingtonCompanies.map((company) => ({
    label: company,
    value: company,
  })),
  [DemoRegion.LOS_ANGELES_CA]: demoLosAngelesCompanies.map((company) => ({
    label: company,
    value: company,
  })),
  [DemoRegion.LOUISVILLE_KY]: demoLouisvilleCompanies.map((company) => ({
    label: company,
    value: company,
  })),
  [DemoRegion.MOBILE_AL]: demoMobileCompanies.map((company) => ({
    label: company,
    value: company,
  })),
  [DemoRegion.NASHVILLE_TN]: demoNashvilleCompanies.map((company) => ({
    label: company,
    value: company,
  })),
};

export const laborupGearColor = "#006ADC";

export const monthOptions = [
  { label: "January", value: "January" },
  { label: "February", value: "February" },
  { label: "March", value: "March" },
  { label: "April", value: "April" },
  { label: "May", value: "May" },
  { label: "June", value: "June" },
  { label: "July", value: "July" },
  { label: "August", value: "August" },
  { label: "September", value: "September" },
  { label: "October", value: "October" },
  { label: "November", value: "November" },
  { label: "December", value: "December" },
];

// * IMPORTANT: This should be kept in sync with the sources on the talent side here
// * with the exception of 'Admin Onboard', 'Google Search Ad', 'Indeed' and 'ZipRecruiter':
// * https://github.com/Laborup/laborup-app/blob/master/web/src/lib/constants.ts#L215
export const sources = [
  "Indeed",
  "ZipRecruiter",
  "Admin Onboard",
  "Google Search Ad",
  "Social Media",
  "Billboard",
  "Flyer / Mail",
  "Job Board",
  "Referral / Friend",
  "Google Search",
  "Event",
  "Other",
];

// Sources that Staffers are allowed to manually select when creating candidacies.
export const stafferSelectableCandidacySources: CandidacySource[] = [
  CandidacySource.INDEED,
  CandidacySource.LINKEDIN,
  CandidacySource.MONSTER,
  CandidacySource.OTHER,
  CandidacySource.REFERRAL,
  CandidacySource.STAFFERDIRECT,
  CandidacySource.ZIPRECRUITER,
];

export const stateOptions = [
  { value: "AL", label: "AL" },
  { value: "AK", label: "AK" },
  { value: "AZ", label: "AZ" },
  { value: "AR", label: "AR" },
  { value: "CA", label: "CA" },
  { value: "CO", label: "CO" },
  { value: "CT", label: "CT" },
  { value: "DC", label: "DC" },
  { value: "DE", label: "DE" },
  { value: "FL", label: "FL" },
  { value: "GA", label: "GA" },
  { value: "HI", label: "HI" },
  { value: "ID", label: "ID" },
  { value: "IL", label: "IL" },
  { value: "IN", label: "IN" },
  { value: "IA", label: "IA" },
  { value: "KS", label: "KS" },
  { value: "KY", label: "KY" },
  { value: "LA", label: "LA" },
  { value: "ME", label: "ME" },
  { value: "MD", label: "MD" },
  { value: "MA", label: "MA" },
  { value: "MI", label: "MI" },
  { value: "MN", label: "MN" },
  { value: "MS", label: "MS" },
  { value: "MO", label: "MO" },
  { value: "MT", label: "MT" },
  { value: "NE", label: "NE" },
  { value: "NV", label: "NV" },
  { value: "NH", label: "NH" },
  { value: "NJ", label: "NJ" },
  { value: "NM", label: "NM" },
  { value: "NY", label: "NY" },
  { value: "NC", label: "NC" },
  { value: "ND", label: "ND" },
  { value: "OH", label: "OH" },
  { value: "OK", label: "OK" },
  { value: "OR", label: "OR" },
  { value: "PA", label: "PA" },
  { value: "RI", label: "RI" },
  { value: "SC", label: "SC" },
  { value: "SD", label: "SD" },
  { value: "TN", label: "TN" },
  { value: "TX", label: "TX" },
  { value: "UT", label: "UT" },
  { value: "VT", label: "VT" },
  { value: "VA", label: "VA" },
  { value: "WA", label: "WA" },
  { value: "WV", label: "WV" },
  { value: "WI", label: "WI" },
  { value: "WY", label: "WY" },
];

export const tiptapDefaultJson = {
  type: "doc" as const,
  content: [
    {
      type: "paragraph",
    },
  ],
};

// MAPS //

export const callStatusToLabel: Record<CallStatus, string> = {
  [CallStatus.QUEUED]: "Queued",
  [CallStatus.INITIATED]: "Initiated",
  [CallStatus.RINGING]: "Ringing",
  [CallStatus.IN_PROGRESS]: "In progress",
  [CallStatus.COMPLETED]: "Completed",
  [CallStatus.BUSY]: "Busy",
  [CallStatus.FAILED]: "Failed",
  [CallStatus.NO_ANSWER]: "No answer",
  [CallStatus.CANCELED]: "Canceled",
  [CallStatus.MISSED]: "Missed",
  [CallStatus.ANSWERED]: "Answered",
  [CallStatus.FORWARDED]: "Forwarded",
  [CallStatus.ABANDONED]: "Abandoned",
};

export const callStatusToBadgeClasses: Record<CallStatus, string> = {
  [CallStatus.COMPLETED]: "border-green-300 bg-green-50 text-green-700",
  [CallStatus.ANSWERED]: "border-green-300 bg-green-50 text-green-700",
  [CallStatus.IN_PROGRESS]: "border-blue-300 bg-blue-50 text-blue-700",
  [CallStatus.RINGING]: "border-blue-300 bg-blue-50 text-blue-700",
  [CallStatus.INITIATED]: "border-slate-300 bg-slate-50 text-slate-700",
  [CallStatus.QUEUED]: "border-slate-300 bg-slate-50 text-slate-700",
  [CallStatus.NO_ANSWER]: "border-amber-300 bg-amber-50 text-amber-700",
  [CallStatus.MISSED]: "border-amber-300 bg-amber-50 text-amber-700",
  [CallStatus.BUSY]: "border-amber-300 bg-amber-50 text-amber-700",
  [CallStatus.FORWARDED]: "border-blue-300 bg-blue-50 text-blue-700",
  [CallStatus.FAILED]: "border-red-300 bg-red-50 text-red-700",
  [CallStatus.CANCELED]: "border-red-300 bg-red-50 text-red-700",
  [CallStatus.ABANDONED]: "border-red-300 bg-red-50 text-red-700",
};

export const accessRoleToLabel: Record<Role, string> = {
  [Role.EMPLOYER]: "Employer",
  [Role.STAFFER]: "Staffer",
};

export const accessRoleOptions: Role[] = [Role.STAFFER, Role.EMPLOYER];

export const userRoleToLabel: Record<UserRole, string> = {
  [UserRole.STAFFER]: "Staffer",
  [UserRole.SALES]: "Sales",
  [UserRole.CSM]: "CSM",
  [UserRole.ADMIN]: "Admin",
  [UserRole.PRODUCT]: "Product",
};

export const userRoleBadgeClasses: Record<UserRole, string> = {
  [UserRole.STAFFER]: "border-blue-300 bg-blue-50 text-blue-700",
  [UserRole.SALES]: "border-green-300 bg-green-50 text-green-700",
  [UserRole.CSM]: "border-violet-300 bg-violet-50 text-violet-700",
  [UserRole.ADMIN]: "border-amber-300 bg-amber-50 text-amber-700",
  [UserRole.PRODUCT]: "border-slate-300 bg-slate-50 text-slate-700",
};

// Roles offered in the manage-users role picker.
export const userRoleOptions: UserRole[] = [
  UserRole.STAFFER,
  UserRole.SALES,
  UserRole.CSM,
  UserRole.ADMIN,
  UserRole.PRODUCT,
];

// Keep in sync with guac candidacySourceToLabel.
export const candidacySourceToLabel: Record<CandidacySource, string> = {
  [CandidacySource.CCM]: "Continuous Candidate Matching",
  [CandidacySource.HIGHMATCH]: "High Match",
  [CandidacySource.LABORUPSEARCH]: "Laborup Search",
  [CandidacySource.LABORUPDIRECT]: "Laborup Direct",
  [CandidacySource.LABORUPCAMPAIGN]: "Laborup Campaign",
  [CandidacySource.INDEED]: "Indeed",
  [CandidacySource.LINKEDIN]: "LinkedIn",
  [CandidacySource.CRAIGSLISTAD]: "Craigslist Ad",
  [CandidacySource.GOOGLESEARCHAD]: "Google Search Ad",
  [CandidacySource.METAAD]: "Meta Ad",
  [CandidacySource.MONSTER]: "Monster",
  [CandidacySource.STAFFERDIRECT]: "Staffer Direct",
  [CandidacySource.STEVIEONBOARDING]: "Stevie Onboarding",
  [CandidacySource.WORKABLE]: "Jobs by Workable",
  [CandidacySource.ZIPRECRUITER]: "ZipRecruiter",
  [CandidacySource.REFERRAL]: "Referral",
  [CandidacySource.OTHER]: "Other",
  [CandidacySource.SMARTSOURCING]: "Smart Sourcing",
  [CandidacySource.INTERNAL_SOURCING]: "Internal Sourcing",
  [CandidacySource.INTERNAL_RECOMMENDATION]: "Internal Recommendation",
  [CandidacySource.JUICEBOX_RESUME_UNLOCK]: "Juicebox Resume Unlock",
  [CandidacySource.MONSTER_RESUME_UNLOCK]: "Monster Resume Unlock",
  [CandidacySource.ZIPRECRUITER_RESUME_UNLOCK]: "ZipRecruiter Resume Unlock",
};

// Keep in sync with guac CANDIDACY_SOURCE_ORIGIN (admin-mixpanel.service.ts).
// Internal = our own network / product. External = paid platforms / ad sources.
export const candidacySourceToOrigin: Record<
  CandidacySource,
  "internal" | "external"
> = {
  [CandidacySource.INTERNAL_SOURCING]: "internal",
  [CandidacySource.INTERNAL_RECOMMENDATION]: "internal",
  [CandidacySource.LABORUPSEARCH]: "internal",
  [CandidacySource.LABORUPDIRECT]: "internal",
  [CandidacySource.LABORUPCAMPAIGN]: "internal",
  [CandidacySource.HIGHMATCH]: "internal",
  [CandidacySource.CCM]: "internal",
  [CandidacySource.STAFFERDIRECT]: "internal",
  [CandidacySource.REFERRAL]: "internal",
  [CandidacySource.STEVIEONBOARDING]: "internal",
  [CandidacySource.INDEED]: "external",
  [CandidacySource.LINKEDIN]: "external",
  [CandidacySource.ZIPRECRUITER]: "external",
  [CandidacySource.CRAIGSLISTAD]: "external",
  [CandidacySource.GOOGLESEARCHAD]: "external",
  [CandidacySource.METAAD]: "external",
  [CandidacySource.MONSTER]: "external",
  [CandidacySource.WORKABLE]: "external",
  [CandidacySource.OTHER]: "external",
  [CandidacySource.SMARTSOURCING]: "external",
  [CandidacySource.MONSTER_RESUME_UNLOCK]: "external",
  [CandidacySource.ZIPRECRUITER_RESUME_UNLOCK]: "external",
  [CandidacySource.JUICEBOX_RESUME_UNLOCK]: "external",
};

export const candidacyStatusToBackgroundColor: Record<CandidacyStatus, string> =
  {
    [CandidacyStatus.APPLIED]: "bg-gray-200",
    [CandidacyStatus.SHORTLIST]: "bg-gray-200",
    [CandidacyStatus.OUTREACH]: "bg-slate-500",
    [CandidacyStatus.FOLLOWINGUP]: "bg-slate-500",
    [CandidacyStatus.ENGAGED]: "bg-slate-500",
    [CandidacyStatus.SCREENSCHEDULED]: "bg-sky-400",
    [CandidacyStatus.SCREENED]: "bg-sky-600",
    [CandidacyStatus.TOREVIEW]: "bg-amber-500",
    [CandidacyStatus.ENRICHANDCHECK]: "bg-amber-500",
    [CandidacyStatus.READYTOSEND]: "bg-purple-600",
    [CandidacyStatus.BATCHSENT]: "bg-purple-600",
    [CandidacyStatus.MATCHED]: "bg-purple-600",
    [CandidacyStatus.INTERVIEW]: "bg-purple-600",
    [CandidacyStatus.OFFER]: "bg-green-600",
    [CandidacyStatus.REJECTED]: "bg-red-600",
    [CandidacyStatus.PLACED]: "bg-green-600",
    [CandidacyStatus.EMPLOYED]: "bg-green-600",
    [CandidacyStatus.EMPLOYMENT_ENDED]: "bg-red-600",
    [CandidacyStatus.CLOSED]: "bg-red-600",
    [CandidacyStatus.COULD_NOT_CONTACT]: "bg-red-600",
  };

// Dashboard Finalists column: candidates still in play for a placement.
// PLACED/EMPLOYED are deliberately out — those are already closed.
export const FINALIST_STATUSES = [
  CandidacyStatus.INTERVIEW,
  CandidacyStatus.OFFER,
];

// Dashboard Submissions column: candidates currently sitting with the customer.
export const SUBMISSION_STATUSES = [CandidacyStatus.BATCHSENT];

export const candidacyStatusToLabel: Record<CandidacyStatus, string> = {
  [CandidacyStatus.APPLIED]: "Applied",
  [CandidacyStatus.SHORTLIST]: "Shortlist",
  [CandidacyStatus.OUTREACH]: "Outreach",
  [CandidacyStatus.FOLLOWINGUP]: "Following Up",
  [CandidacyStatus.ENGAGED]: "Engaged",
  [CandidacyStatus.SCREENSCHEDULED]: "Screen Scheduled",
  [CandidacyStatus.SCREENED]: "Screened",
  [CandidacyStatus.TOREVIEW]: "To Review",
  [CandidacyStatus.ENRICHANDCHECK]: "Enrich and Check",
  [CandidacyStatus.READYTOSEND]: "Ready to Send",
  [CandidacyStatus.BATCHSENT]: "Batch Sent",
  [CandidacyStatus.MATCHED]: "Matched",
  [CandidacyStatus.INTERVIEW]: "Interview",
  [CandidacyStatus.OFFER]: "Offer",
  [CandidacyStatus.REJECTED]: "Rejected",
  [CandidacyStatus.PLACED]: "Placed",
  [CandidacyStatus.EMPLOYED]: "Employed",
  [CandidacyStatus.EMPLOYMENT_ENDED]: "Employment Ended",
  [CandidacyStatus.CLOSED]: "Closed",
  [CandidacyStatus.COULD_NOT_CONTACT]: "Could Not Contact",
};

export const sourcedTypeToLabel: Record<SourcedType, string> = {
  [SourcedType.INTERNAL]: "Internally Sourced",
  [SourcedType.EXTERNAL]: "Externally Sourced",
  [SourcedType.RECOMMENDED]: "Recommended",
  [SourcedType.RESURRECTED]: "Resurrected",
  [SourcedType.STEVIE_NUDGED]: "Stevie Nudged",
};

export const sourcedTypeToDotColor: Record<SourcedType, string> = {
  [SourcedType.INTERNAL]: "bg-slate-500",
  [SourcedType.EXTERNAL]: "bg-primary-600",
  [SourcedType.RECOMMENDED]: "bg-emerald-600",
  [SourcedType.RESURRECTED]: "bg-fuchsia-600",
  [SourcedType.STEVIE_NUDGED]: "bg-sky-600",
};

export const SCREEN_HOURS_GREEN_MAX = 12;
export const SCREEN_HOURS_RED_MIN = 24;

// Tiers for staffer stats submits
export const SUBMITS_WEEK_GREEN_MIN = 3;
export const SUBMITS_WEEK_ORANGE_MIN = 2;
export const SUBMITS_MONTH_GREEN_MIN = 12;
export const SUBMITS_MONTH_ORANGE_MIN = 6;

// Staffer Stats chart geometry, as a percentage down from the chart's top.
export const STAFFER_STAT_CHART_TOP_PERCENT = 8;
export const STAFFER_STAT_CHART_BOTTOM_PERCENT = 78;
export const STAFFER_STAT_GOAL_LINE_PERCENT = 50;

// Each dashboard goal panel is its own query container, so the value scales
// with the panel and the goal denominator drops under it once the panel is too
// narrow to sit beside it.
export const GOAL_PANEL_CONTAINER_CLASS = "[container-type:inline-size]";
export const GOAL_PANEL_VALUE_CLASS = "text-[clamp(1.5rem,24cqi,2.25rem)]";
export const GOAL_PANEL_NARROW_STACK_CLASS =
  "[@container(max-width:170px)]:flex-col [@container(max-width:170px)]:items-start [@container(max-width:170px)]:gap-1";
export const GOAL_PANEL_NARROW_LABEL_CLASS =
  "[@container(max-width:170px)]:pb-0";

// Submits and revenue hang their goal line near the top of the plot, so the
// space below it carries the whole run-up to goal and the fluctuation reads.
// Time to screen keeps the mid-card line above.
export const KPI_GOAL_LINE_PERCENT = 7.4;
// Past this multiple of goal the mark is well clear of the card; capped only so
// the DOM never gets a silly number.
export const KPI_MAX_GOAL_RATIO = 3;

// Weekly (Mon–Sun) Laborup Search goals. Mirror guac's constants: the server
// pays the coin bonus when the week-to-date count reaches these.
export const WEEKLY_INTEREST_GOAL = 50;
export const WEEKLY_SHORTLIST_GOAL = 25;

// Text color for a graded stat, shared by the dashboard stat chart cards.
export const performanceTierTextClass: Record<PerformanceTier, string> = {
  [PerformanceTier.GREEN]: "text-[#1D7A42] dark:text-[#DCEBDA]",
  [PerformanceTier.ORANGE]: "text-[#A85818] dark:text-[#FFC9AB]",
  [PerformanceTier.RED]: "text-[#9B3030] dark:text-[#E2C4BC]",
};

// Gradient bar fill for a graded stat (classes defined in index.css).
export const performanceTierGradientClass: Record<PerformanceTier, string> = {
  [PerformanceTier.GREEN]: "performance-gradient-green",
  [PerformanceTier.ORANGE]: "performance-gradient-orange",
  [PerformanceTier.RED]: "performance-gradient-red",
};

// Grades an hours-to-first-screen against the 12h/24h standard.
export const screenTimeTier = (hours: number): PerformanceTier => {
  if (hours > SCREEN_HOURS_RED_MIN) return PerformanceTier.RED;
  if (hours > SCREEN_HOURS_GREEN_MAX) return PerformanceTier.ORANGE;
  return PerformanceTier.GREEN;
};

// Grades a time-to-screen: fast is green, a day is yellow, slower reads as
// plainly bad. Shared by the job pipeline header and the staffer stats card.
export const screenTimeColorClass = (hours: number): string => {
  if (hours > SCREEN_HOURS_RED_MIN) return "text-red-600";
  if (hours > SCREEN_HOURS_GREEN_MAX) return "text-yellow-400";
  return "text-emerald-600";
};

// How a candidacy got to the Applied stage, for its badge. Resurrection wins —
// it's why the candidacy is back in Applied at all, which matters more than how
// it first arrived. Internal wins the remaining ties. Returns null when the
// candidacy applied on its own (no badge). Back from a Stevie call beats all of
// them: the reviewer needs to know there's new information to look at.
export const getSourcedType = (
  source: CandidacySource | null,
  isExternallySourced: boolean,
  isAutoResurrected: boolean,
  isBackFromStevieCall = false,
): SourcedType | null => {
  if (isBackFromStevieCall) {
    return SourcedType.STEVIE_NUDGED;
  }
  if (isAutoResurrected) {
    return SourcedType.RESURRECTED;
  }
  if (source === CandidacySource.INTERNAL_SOURCING) {
    return SourcedType.INTERNAL;
  }
  if (source === CandidacySource.INTERNAL_RECOMMENDATION) {
    return SourcedType.RECOMMENDED;
  }
  if (isExternallySourced) {
    return SourcedType.EXTERNAL;
  }
  return null;
};

export const pipelineStageOrder: PipelineStage[] = [
  PipelineStage.APPLIED,
  PipelineStage.IN_COMMUNICATION,
  PipelineStage.SCREEN_SCHEDULED,
  PipelineStage.TO_REVIEW,
  PipelineStage.PREP_TO_SEND,
  PipelineStage.SUBMITTED,
  PipelineStage.INTERVIEW,
  PipelineStage.PLACED,
];

export const pipelineStageToLabel: Record<PipelineStage, string> = {
  [PipelineStage.APPLIED]: "Applied",
  [PipelineStage.IN_COMMUNICATION]: "In Communication",
  [PipelineStage.SCREEN_SCHEDULED]: "Screening",
  [PipelineStage.TO_REVIEW]: "To Review",
  [PipelineStage.PREP_TO_SEND]: "Prep to Send",
  [PipelineStage.SUBMITTED]: "Submitted",
  [PipelineStage.INTERVIEW]: "Interview",
  [PipelineStage.PLACED]: "Placed",
};

export const pipelineStageToKanbanToneClass: Record<PipelineStage, string> = {
  // Grey gradient from Applied to Screen Scheduled
  // Ensure always that middle values are linear interpolations between the endpoints
  [PipelineStage.APPLIED]: "bg-[#F4F5F6] dark:bg-[#212327]",
  [PipelineStage.IN_COMMUNICATION]: "bg-[#E4E6E8] dark:bg-[#37393D]",
  [PipelineStage.SCREEN_SCHEDULED]: "bg-[#D4D7DB] dark:bg-[#4C4F52]",

  // Green gradient from To Review to Placed
  // Ensure always that middle values are linear interpolations between the endpoints
  [PipelineStage.TO_REVIEW]: "bg-[#E2EBE6] dark:bg-[#1D231B]",
  [PipelineStage.PREP_TO_SEND]: "bg-[#CBDED5] dark:bg-[#242A21]",
  [PipelineStage.SUBMITTED]: "bg-[#B4D0C4] dark:bg-[#2B3127]",
  [PipelineStage.INTERVIEW]: "bg-[#9CC3B2] dark:bg-[#31372D]",
  [PipelineStage.PLACED]: "bg-[#85B5A1] dark:bg-[#383E33]",
};

export const KANBAN_COLUMN_WIDTH_CLASS = "w-[332px]";

export const pipelineStageToKanbanDescription: Record<PipelineStage, string> = {
  [PipelineStage.APPLIED]: "New applicants ready for an initial review.",
  [PipelineStage.IN_COMMUNICATION]: "Communicating via call or text/email.",
  [PipelineStage.SCREEN_SCHEDULED]: "Phone screen booked or completed.",
  [PipelineStage.TO_REVIEW]:
    "Call references and enrich the candidate's profile.",
  [PipelineStage.PREP_TO_SEND]: "Enrich the profile and queue for submission.",
  [PipelineStage.SUBMITTED]: "Sent to the client. Awaiting their feedback.",
  [PipelineStage.INTERVIEW]:
    "Interviews scheduled or in progress with the client.",
  [PipelineStage.PLACED]:
    "Offer through onboarding. Track acceptance and start.",
};

export const pipelineStageToKanbanEmptyState: Record<PipelineStage, string> = {
  [PipelineStage.APPLIED]: "No birds in the nest",
  [PipelineStage.IN_COMMUNICATION]: "No candidates yet.",
  [PipelineStage.SCREEN_SCHEDULED]: "No candidates yet.",
  [PipelineStage.TO_REVIEW]: "No candidates yet.",
  [PipelineStage.PREP_TO_SEND]: "No candidates yet.",
  [PipelineStage.SUBMITTED]: "No candidates yet.",
  [PipelineStage.INTERVIEW]: "No candidates yet.",
  [PipelineStage.PLACED]: "No candidates yet.",
};

// Everything at or before Enrich and Check. Moving a candidacy out of one of
// these while its job is paused or covered is what prompts to reactivate the
// job — the pipeline is being worked, so the job is evidently live.
export const PRE_SUBMISSION_CANDIDACY_STATUSES: CandidacyStatus[] = [
  CandidacyStatus.APPLIED,
  CandidacyStatus.SHORTLIST,
  CandidacyStatus.OUTREACH,
  CandidacyStatus.FOLLOWINGUP,
  CandidacyStatus.ENGAGED,
  CandidacyStatus.SCREENSCHEDULED,
  CandidacyStatus.SCREENED,
  CandidacyStatus.TOREVIEW,
  CandidacyStatus.ENRICHANDCHECK,
];

// Moving someone here is clearing them off the job, not working it, so it never
// prompts to reactivate a parked job.
export const TERMINAL_CANDIDACY_STATUSES: CandidacyStatus[] = [
  CandidacyStatus.REJECTED,
  CandidacyStatus.CLOSED,
  CandidacyStatus.COULD_NOT_CONTACT,
  CandidacyStatus.EMPLOYMENT_ENDED,
];

// Statuses that don't bucket directly into a pipeline stage (ie hidden by default in pipeline)
export const hiddenPipelineCandidacyStatuses = TERMINAL_CANDIDACY_STATUSES;

export const candidacyStatusToPipelineStage: Record<
  CandidacyStatus,
  PipelineStage | null
> = {
  [CandidacyStatus.APPLIED]: PipelineStage.APPLIED,
  [CandidacyStatus.SHORTLIST]: PipelineStage.IN_COMMUNICATION,
  [CandidacyStatus.OUTREACH]: PipelineStage.IN_COMMUNICATION,
  [CandidacyStatus.FOLLOWINGUP]: PipelineStage.IN_COMMUNICATION,
  [CandidacyStatus.ENGAGED]: PipelineStage.IN_COMMUNICATION,
  [CandidacyStatus.SCREENSCHEDULED]: PipelineStage.SCREEN_SCHEDULED,
  [CandidacyStatus.SCREENED]: PipelineStage.SCREEN_SCHEDULED,
  [CandidacyStatus.TOREVIEW]: PipelineStage.TO_REVIEW,
  [CandidacyStatus.ENRICHANDCHECK]: PipelineStage.PREP_TO_SEND,
  [CandidacyStatus.READYTOSEND]: PipelineStage.PREP_TO_SEND,
  [CandidacyStatus.BATCHSENT]: PipelineStage.SUBMITTED,
  [CandidacyStatus.MATCHED]: PipelineStage.INTERVIEW,
  [CandidacyStatus.INTERVIEW]: PipelineStage.INTERVIEW,
  [CandidacyStatus.OFFER]: PipelineStage.PLACED,
  [CandidacyStatus.PLACED]: PipelineStage.PLACED,
  [CandidacyStatus.EMPLOYED]: PipelineStage.PLACED,
  // Hidden statuses don't have a stage — bucketer falls back to fromStatus.
  [CandidacyStatus.REJECTED]: null,
  [CandidacyStatus.CLOSED]: null,
  [CandidacyStatus.COULD_NOT_CONTACT]: null,
  [CandidacyStatus.EMPLOYMENT_ENDED]: null,
};

// Statuses REJECTED/CLOSED/COULD_NOT_CONTACT fall back to POST_SCREEN; the
// reject action is disabled for them anyway.
export const candidacyStatusToRejectionReasonGroup: Record<
  CandidacyStatus,
  CandidacyRejectionReasonGroup
> = {
  [CandidacyStatus.APPLIED]: CandidacyRejectionReasonGroup.PROFILE_READ,
  [CandidacyStatus.SHORTLIST]: CandidacyRejectionReasonGroup.PROFILE_READ,
  [CandidacyStatus.OUTREACH]:
    CandidacyRejectionReasonGroup.COMMUNICATION_SCREEN,
  [CandidacyStatus.FOLLOWINGUP]:
    CandidacyRejectionReasonGroup.COMMUNICATION_SCREEN,
  [CandidacyStatus.ENGAGED]: CandidacyRejectionReasonGroup.COMMUNICATION_SCREEN,
  [CandidacyStatus.SCREENSCHEDULED]:
    CandidacyRejectionReasonGroup.COMMUNICATION_SCREEN,
  [CandidacyStatus.SCREENED]: CandidacyRejectionReasonGroup.POST_SCREEN,
  [CandidacyStatus.TOREVIEW]: CandidacyRejectionReasonGroup.POST_SCREEN,
  [CandidacyStatus.ENRICHANDCHECK]: CandidacyRejectionReasonGroup.POST_SCREEN,
  [CandidacyStatus.READYTOSEND]: CandidacyRejectionReasonGroup.POST_SCREEN,
  [CandidacyStatus.BATCHSENT]: CandidacyRejectionReasonGroup.SUBMITTED,
  [CandidacyStatus.MATCHED]: CandidacyRejectionReasonGroup.SUBMITTED,
  [CandidacyStatus.INTERVIEW]: CandidacyRejectionReasonGroup.SUBMITTED,
  [CandidacyStatus.OFFER]: CandidacyRejectionReasonGroup.POST_OFFER,
  [CandidacyStatus.PLACED]: CandidacyRejectionReasonGroup.POST_OFFER,
  [CandidacyStatus.EMPLOYED]: CandidacyRejectionReasonGroup.POST_OFFER,
  [CandidacyStatus.EMPLOYMENT_ENDED]: CandidacyRejectionReasonGroup.POST_OFFER,
  [CandidacyStatus.REJECTED]: CandidacyRejectionReasonGroup.POST_SCREEN,
  [CandidacyStatus.CLOSED]: CandidacyRejectionReasonGroup.POST_SCREEN,
  [CandidacyStatus.COULD_NOT_CONTACT]:
    CandidacyRejectionReasonGroup.POST_SCREEN,
};

// Order within a group drives the 1-5 keyboard shortcuts on the reject form.
export const candidacyRejectionReasonGroupToReasons: Record<
  CandidacyRejectionReasonGroup,
  CandidacyRejectionReason[]
> = {
  [CandidacyRejectionReasonGroup.PROFILE_READ]: [
    CandidacyRejectionReason.SKILLS_GAP,
    CandidacyRejectionReason.LOCATION_COMMUTE,
    CandidacyRejectionReason.PAY_MISALIGNMENT,
    CandidacyRejectionReason.SCHEDULE_SHIFT_CONFLICT,
    CandidacyRejectionReason.TENURE,
    CandidacyRejectionReason.OTHER,
  ],
  [CandidacyRejectionReasonGroup.COMMUNICATION_SCREEN]: [
    CandidacyRejectionReason.COULD_NOT_CONTACT,
    CandidacyRejectionReason.WITHDREW_INTEREST,
    CandidacyRejectionReason.SKILLS_GAP,
    CandidacyRejectionReason.LOCATION_COMMUTE,
    CandidacyRejectionReason.PAY_MISALIGNMENT,
    CandidacyRejectionReason.SCHEDULE_SHIFT_CONFLICT,
    CandidacyRejectionReason.TENURE,
    CandidacyRejectionReason.OTHER,
  ],
  [CandidacyRejectionReasonGroup.POST_SCREEN]: [
    CandidacyRejectionReason.COULD_NOT_CONTACT,
    CandidacyRejectionReason.WITHDREW_INTEREST,
    CandidacyRejectionReason.SKILLS_GAP,
    CandidacyRejectionReason.PAY_MISALIGNMENT,
    CandidacyRejectionReason.SCHEDULE_SHIFT_CONFLICT,
    CandidacyRejectionReason.LOCATION_COMMUTE,
    CandidacyRejectionReason.TENURE,
    CandidacyRejectionReason.BACKGROUND_QUALIFICATIONS_CONCERN,
    CandidacyRejectionReason.SOFT_SKILLS_FIT,
    CandidacyRejectionReason.MOTIVATION,
    CandidacyRejectionReason.OTHER,
  ],
  [CandidacyRejectionReasonGroup.SUBMITTED]: [
    CandidacyRejectionReason.FOUND_BETTER_FIT_CANDIDATE,
    CandidacyRejectionReason.CUSTOMER_FEEDBACK_REDIRECTED,
    CandidacyRejectionReason.CUSTOMER_REJECTED_CANDIDATE,
    CandidacyRejectionReason.CANDIDATE_BECAME_UNRESPONSIVE,
    CandidacyRejectionReason.CANDIDATE_WITHDREW_INTEREST,
    CandidacyRejectionReason.OTHER,
  ],
  [CandidacyRejectionReasonGroup.POST_OFFER]: [
    CandidacyRejectionReason.CANDIDATE_DECLINED_OFFER,
    CandidacyRejectionReason.BACKGROUND_DRUG_CHECK_FAILED,
    CandidacyRejectionReason.COMP_NEGOTIATION_FAILED,
    CandidacyRejectionReason.ACCEPTED_COMPETING_OFFER,
    CandidacyRejectionReason.CUSTOMER_PULLED_OFFER,
    CandidacyRejectionReason.OTHER,
  ],
};

export const candidacyRejectionReasonToTitle: Record<
  CandidacyRejectionReason,
  string
> = {
  [CandidacyRejectionReason.SKILLS_GAP]: "Skills gap",
  [CandidacyRejectionReason.LOCATION_COMMUTE]: "Location / commute",
  [CandidacyRejectionReason.PAY_MISALIGNMENT]: "Pay misalignment",
  [CandidacyRejectionReason.SCHEDULE_SHIFT_CONFLICT]:
    "Schedule / shift conflict",
  [CandidacyRejectionReason.TENURE]: "Tenure",
  [CandidacyRejectionReason.COULD_NOT_CONTACT]: "Could not contact",
  [CandidacyRejectionReason.WITHDREW_INTEREST]: "Withdrew interest",
  [CandidacyRejectionReason.BACKGROUND_QUALIFICATIONS_CONCERN]:
    "Background / qualifications concern",
  [CandidacyRejectionReason.SOFT_SKILLS_FIT]: "Soft skills / fit",
  [CandidacyRejectionReason.MOTIVATION]: "Motivation",
  [CandidacyRejectionReason.FOUND_BETTER_FIT_CANDIDATE]:
    "Found better-fit candidate",
  [CandidacyRejectionReason.CUSTOMER_FEEDBACK_REDIRECTED]:
    "Customer feedback redirected the search",
  [CandidacyRejectionReason.CUSTOMER_REJECTED_CANDIDATE]:
    "Customer rejected candidate",
  [CandidacyRejectionReason.CANDIDATE_BECAME_UNRESPONSIVE]:
    "Candidate became unresponsive",
  [CandidacyRejectionReason.CANDIDATE_WITHDREW_INTEREST]:
    "Candidate withdrew interest",
  [CandidacyRejectionReason.CANDIDATE_DECLINED_OFFER]:
    "Candidate declined the offer",
  [CandidacyRejectionReason.BACKGROUND_DRUG_CHECK_FAILED]:
    "Background or drug check failed",
  [CandidacyRejectionReason.COMP_NEGOTIATION_FAILED]: "Comp negotiation failed",
  [CandidacyRejectionReason.ACCEPTED_COMPETING_OFFER]:
    "Accepted a competing offer",
  [CandidacyRejectionReason.CUSTOMER_PULLED_OFFER]: "Customer pulled the offer",
  [CandidacyRejectionReason.OTHER]: "Other",
};

export const candidacyRejectionReasonToSubtitle: Record<
  CandidacyRejectionReason,
  string
> = {
  [CandidacyRejectionReason.SKILLS_GAP]: "Experience does not match the role",
  [CandidacyRejectionReason.LOCATION_COMMUTE]: "Commute is not sustainable",
  [CandidacyRejectionReason.PAY_MISALIGNMENT]:
    "Pay expectations are outside the role's range",
  [CandidacyRejectionReason.SCHEDULE_SHIFT_CONFLICT]:
    "Required schedule does not work",
  [CandidacyRejectionReason.TENURE]:
    "Job-hopping pattern or stay-length mismatch",
  [CandidacyRejectionReason.COULD_NOT_CONTACT]:
    "No response after repeated attempts",
  [CandidacyRejectionReason.WITHDREW_INTEREST]:
    "Candidate is no longer interested",
  [CandidacyRejectionReason.BACKGROUND_QUALIFICATIONS_CONCERN]:
    "Drug test or background check concern",
  [CandidacyRejectionReason.SOFT_SKILLS_FIT]:
    "Misalignment on attitude, personality, coachability, or other soft skills",
  [CandidacyRejectionReason.MOTIVATION]:
    "Unclear motivation or it does not align with customer values",
  [CandidacyRejectionReason.FOUND_BETTER_FIT_CANDIDATE]:
    "Another candidate fits this req better",
  [CandidacyRejectionReason.CUSTOMER_FEEDBACK_REDIRECTED]:
    "Customer feedback changed what the req needs",
  [CandidacyRejectionReason.CUSTOMER_REJECTED_CANDIDATE]:
    "Customer passed after reviewing the candidate",
  [CandidacyRejectionReason.CANDIDATE_BECAME_UNRESPONSIVE]:
    "Stopped responding after submission",
  [CandidacyRejectionReason.CANDIDATE_WITHDREW_INTEREST]:
    "Pulled out after being submitted",
  [CandidacyRejectionReason.CANDIDATE_DECLINED_OFFER]:
    "Turned down the extended offer",
  [CandidacyRejectionReason.BACKGROUND_DRUG_CHECK_FAILED]:
    "Did not pass post-offer screening",
  [CandidacyRejectionReason.COMP_NEGOTIATION_FAILED]:
    "Could not agree on compensation",
  [CandidacyRejectionReason.ACCEPTED_COMPETING_OFFER]:
    "Took an offer somewhere else",
  [CandidacyRejectionReason.CUSTOMER_PULLED_OFFER]:
    "Customer withdrew the extended offer",
  [CandidacyRejectionReason.OTHER]: "Add a note with the specifics",
};

export const candidacyRejectionReasonGroupToNotePlaceholder: Record<
  CandidacyRejectionReasonGroup,
  string
> = {
  [CandidacyRejectionReasonGroup.PROFILE_READ]:
    "Add context about the profile mismatch",
  [CandidacyRejectionReasonGroup.COMMUNICATION_SCREEN]:
    "Add context from outreach or screening",
  [CandidacyRejectionReasonGroup.POST_SCREEN]:
    "Add context from the completed screen",
  [CandidacyRejectionReasonGroup.SUBMITTED]:
    "Add context from the customer or candidate",
  [CandidacyRejectionReasonGroup.POST_OFFER]:
    "Add context about the unsuccessful offer",
};

// Post-offer rejections also close out the placement; the reason picks
// whether the candidate declined or the offer was revoked.
export const candidacyRejectionReasonToPlacementStatus: Partial<
  Record<
    CandidacyRejectionReason,
    PlacementStatus.REJECTED | PlacementStatus.REVOKED
  >
> = {
  [CandidacyRejectionReason.CANDIDATE_DECLINED_OFFER]: PlacementStatus.REJECTED,
  [CandidacyRejectionReason.ACCEPTED_COMPETING_OFFER]: PlacementStatus.REJECTED,
  [CandidacyRejectionReason.BACKGROUND_DRUG_CHECK_FAILED]:
    PlacementStatus.REVOKED,
  [CandidacyRejectionReason.COMP_NEGOTIATION_FAILED]: PlacementStatus.REVOKED,
  [CandidacyRejectionReason.CUSTOMER_PULLED_OFFER]: PlacementStatus.REVOKED,
};

// Map to first status for each stage bucket - used for add candidate button.
export const pipelineStageToDefaultCandidacyStatus: Record<
  PipelineStage,
  CandidacyStatus
> = {
  [PipelineStage.APPLIED]: CandidacyStatus.APPLIED,
  [PipelineStage.IN_COMMUNICATION]: CandidacyStatus.SHORTLIST,
  [PipelineStage.SCREEN_SCHEDULED]: CandidacyStatus.SCREENSCHEDULED,
  [PipelineStage.TO_REVIEW]: CandidacyStatus.TOREVIEW,
  [PipelineStage.PREP_TO_SEND]: CandidacyStatus.ENRICHANDCHECK,
  [PipelineStage.SUBMITTED]: CandidacyStatus.BATCHSENT,
  [PipelineStage.INTERVIEW]: CandidacyStatus.MATCHED,
  [PipelineStage.PLACED]: CandidacyStatus.OFFER,
};

// Kanban cards and table rows in these stages render an inline sub-status
// pill listing the statuses below. Stages not in the map render no pill.
export const stageChipStatusesByStage: Partial<
  Record<PipelineStage, CandidacyStatus[]>
> = {
  [PipelineStage.IN_COMMUNICATION]: [
    CandidacyStatus.SHORTLIST,
    CandidacyStatus.OUTREACH,
    CandidacyStatus.FOLLOWINGUP,
    CandidacyStatus.ENGAGED,
  ],
  [PipelineStage.SCREEN_SCHEDULED]: [
    CandidacyStatus.SCREENSCHEDULED,
    CandidacyStatus.SCREENED,
  ],
  [PipelineStage.PREP_TO_SEND]: [
    CandidacyStatus.ENRICHANDCHECK,
    CandidacyStatus.READYTOSEND,
  ],
  [PipelineStage.INTERVIEW]: [
    CandidacyStatus.MATCHED,
    CandidacyStatus.INTERVIEW,
  ],
  [PipelineStage.PLACED]: [
    CandidacyStatus.INTERVIEW,
    CandidacyStatus.OFFER,
    CandidacyStatus.PLACED,
    CandidacyStatus.EMPLOYED,
  ],
};

// Statuses whose transition is gated by a confirmation/details dialog rather
// than a direct mutation.
export const STATUS_CHANGE_DIALOG_STATUSES: ReadonlySet<CandidacyStatus> =
  new Set([
    CandidacyStatus.INTERVIEW,
    CandidacyStatus.SCREENSCHEDULED,
    CandidacyStatus.SCREENED,
    CandidacyStatus.OFFER,
    CandidacyStatus.PLACED,
    CandidacyStatus.EMPLOYED,
    CandidacyStatus.EMPLOYMENT_ENDED,
  ]);

// Statuses a candidacy can be rolled back FROM. These are the placement-stage
// statuses where a backward move tears down a placement and/or requisition fill
// (employment-ended is intentionally excluded — it's terminal).
const ROLLBACK_ORIGIN_STATUSES: ReadonlySet<CandidacyStatus> = new Set([
  CandidacyStatus.OFFER,
  CandidacyStatus.PLACED,
]);

// True when moving from -> to is a placement rollback (a backward move out of a
// placement stage) rather than a normal forward/lateral status change.
export const isRollbackTransition = (
  from: CandidacyStatus,
  to: CandidacyStatus,
): boolean =>
  ROLLBACK_ORIGIN_STATUSES.has(from) &&
  candidacyStatusToRank[to] < candidacyStatusToRank[from];

export const candidacyStatusToRank: Record<CandidacyStatus, number> = {
  [CandidacyStatus.APPLIED]: 0,
  [CandidacyStatus.SHORTLIST]: 1,
  [CandidacyStatus.OUTREACH]: 2,
  [CandidacyStatus.FOLLOWINGUP]: 3,
  [CandidacyStatus.ENGAGED]: 4,
  [CandidacyStatus.SCREENSCHEDULED]: 5,
  [CandidacyStatus.SCREENED]: 6,
  [CandidacyStatus.TOREVIEW]: 7,
  [CandidacyStatus.ENRICHANDCHECK]: 8,
  [CandidacyStatus.READYTOSEND]: 9,
  [CandidacyStatus.BATCHSENT]: 10,
  [CandidacyStatus.MATCHED]: 11,
  [CandidacyStatus.INTERVIEW]: 12,
  [CandidacyStatus.OFFER]: 13,
  [CandidacyStatus.REJECTED]: 14,
  [CandidacyStatus.PLACED]: 15,
  [CandidacyStatus.EMPLOYED]: 16,
  [CandidacyStatus.EMPLOYMENT_ENDED]: 17,
  [CandidacyStatus.CLOSED]: 18,
  [CandidacyStatus.COULD_NOT_CONTACT]: 19,
};

export const communicationModeToLabel: Record<CommunicationMode, string> = {
  [CommunicationMode.CALL]: "Call",
  [CommunicationMode.EMAIL]: "Email",
  [CommunicationMode.TEXT]: "Text",
  [CommunicationMode.VIDEO]: "Video",
};

export const companyIndustryToLabel: Record<CompanyIndustry, string> = {
  [CompanyIndustry.ADVANCED_MANUFACTURING]: "Advanced Manufacturing",
  [CompanyIndustry.AEROSPACE_AND_DEFENSE]: "Aerospace & Defense",
  [CompanyIndustry.AUTOMOTIVE]: "Automotive",
  [CompanyIndustry.CONSTRUCTION_MATERIALS]: "Construction Materials",
  [CompanyIndustry.CONSUMER_PRODUCTION]: "Consumer Production",
  [CompanyIndustry.HEAVY_EQUIPMENT_AND_MACHINERY]:
    "Heavy Equipment & Machinery",
  [CompanyIndustry.MATERIALS_PRODUCTION]: "Materials Production",
  [CompanyIndustry.METALS_AND_MACHINING]: "Metals & Machining",
  [CompanyIndustry.MARINE_MANUFACTURING]: "Marine Manufacturing",
  [CompanyIndustry.OIL_AND_GAS]: "Oil & Gas",
  [CompanyIndustry.RENEWABLE_ENERGY]: "Renewable Energy",
  [CompanyIndustry.WATER_AND_WASTE]: "Water & Waste",
};

export const companyIntakeTabToLabel: Record<CompanyIntakeTab, string> = {
  [CompanyIntakeTab.SalesTeam]: "Sales Team",
  [CompanyIntakeTab.Stakeholders]: "Stakeholders",
  [CompanyIntakeTab.GeneralInfo]: "General Info",
  [CompanyIntakeTab.Sites]: "Sites",
  [CompanyIntakeTab.TalentRecruitingHistory]: "Talent Recruiting History",
  [CompanyIntakeTab.Culture]: "Culture",
  [CompanyIntakeTab.Business]: "Business",
};

export const jobIntakeTabToLabel: Record<JobIntakeTab, string> = {
  [JobIntakeTab.Stakeholders]: "Stakeholders",
  [JobIntakeTab.GeneralInfo]: "General Info",
  [JobIntakeTab.TalentRecruitingHistory]: "Talent Recruiting History",
  [JobIntakeTab.Culture]: "Culture",
  [JobIntakeTab.IdealCandidateProfiles]: "Ideal Candidate Profiles",
  [JobIntakeTab.AntiIdealCandidateProfiles]: "Anti-Ideal Candidate Profiles",
  [JobIntakeTab.InterviewProcess]: "Hiring Process",
  [JobIntakeTab.DatesAndUrgency]: "Dates & Urgency",
  [JobIntakeTab.ScreeningQuestions]: "Screening Questions",
};

export const interviewRoundTypeToLabel: Record<InterviewRoundType, string> = {
  [InterviewRoundType.PHONE_SCREEN]: "Phone Screen",
  [InterviewRoundType.VIDEO_INTERVIEW]: "Video Interview",
  [InterviewRoundType.ONSITE]: "Onsite",
  [InterviewRoundType.APTITUDE_TEST]: "Aptitude Test",
  [InterviewRoundType.SKILLS_TEST]: "Skills Test",
  [InterviewRoundType.PANEL_INTERVIEW]: "Panel Interview",
  [InterviewRoundType.EXECUTIVE_INTERVIEW]: "Executive Interview",
};

export const interviewRoundTypeOptions = Object.entries(
  interviewRoundTypeToLabel,
).map(([value, label]) => ({ value: value as InterviewRoundType, label }));

export const interviewTypeKindClasses = {
  [InterviewType.CUSTOMER]: {
    cardBg: "bg-[#A2C9B8]/30",
    stripe: "bg-emerald-700",
    dot: "bg-emerald-700",
    title: "text-emerald-950",
    chip: "bg-white text-emerald-800 ring-1 ring-inset ring-emerald-300",
    avatar:
      "bg-emerald-100 text-emerald-900 ring-1 ring-inset ring-emerald-300 hover:bg-emerald-200 hover:text-emerald-950",
    label: "Interview",
  },
  [InterviewType.INTERNAL]: {
    cardBg: "bg-[#E5E7EA]/45",
    stripe: "bg-zinc-500",
    dot: "bg-[#E5E7EA]",
    title: "text-zinc-900",
    chip: "bg-white text-zinc-700 ring-1 ring-inset ring-zinc-300",
    avatar:
      "bg-zinc-200 text-zinc-900 ring-1 ring-inset ring-zinc-400 hover:bg-zinc-300 hover:text-zinc-900",
    label: "Screen",
  },
};

const compensationTypeToLabel: Record<CompensationType, string> = {
  [CompensationType.HOURLY]: "Hourly",
  [CompensationType.YEARLY]: "Yearly",
};

export const employmentTypeToLabel: Record<EmploymentType, string> = {
  [EmploymentType.CONTRACT]: "Contract",
  [EmploymentType.FULLTIME]: "Full-time",
  [EmploymentType.PARTTIME]: "Part-time",
};

// Ordered best → worst; drives health filter option order.
export const HEALTH_TIER_KEYS: PortfolioHealthScoreTier[] = [
  "healthy",
  "watch",
  "at-risk",
  "critical",
];

export const healthTierToLabel: Record<PortfolioHealthScoreTier, string> = {
  healthy: "Healthy",
  watch: "Watch",
  "at-risk": "At risk",
  critical: "Critical",
};

export const healthTierToBadgeClasses: Record<
  PortfolioHealthScoreTier,
  string
> = {
  healthy:
    "border-[#27AE60] bg-[#27AE60]/15 text-[#1D7A42] dark:text-[#DCEBDA]",
  watch: "border-[#F2C94C] bg-[#F2C94C]/20 text-[#8A7220] dark:text-[#CFCABC]",
  "at-risk":
    "border-[#E67E22] bg-[#E67E22]/15 text-[#A85818] dark:text-[#FFC9AB]",
  critical:
    "border-[#D14343] bg-[#D14343]/15 text-[#9B3030] dark:text-[#E2C4BC]",
};

export const healthTierToHexColor: Record<PortfolioHealthScoreTier, string> = {
  healthy: "#27AE60",
  watch: "#F2C94C",
  "at-risk": "#E67E22",
  critical: "#D14343",
};

export const getHealthTier = (score: number): PortfolioHealthScoreTier => {
  if (score >= 80) return "healthy";
  if (score >= 60) return "watch";
  if (score >= 40) return "at-risk";
  return "critical";
};

export const jobBottleNeckToIcon: Record<JobBottleneck, React.ElementType> = {
  [JobBottleneck.CUSTOMER]: Building2Icon,
  [JobBottleneck.SCREENING]: FilterIcon,
  [JobBottleneck.SOURCING]: SearchIcon,
};

export const jobBottleneckToLabel: Record<JobBottleneck, string> = {
  [JobBottleneck.CUSTOMER]: "Customer",
  [JobBottleneck.SCREENING]: "Screening",
  [JobBottleneck.SOURCING]: "Sourcing",
};

// Order matters: drives option order in the category dropdown, filter, and the
// config form. Professional intentionally sits last.
export const jobCategoryToLabel: Record<JobCategory, string> = {
  [JobCategory.PRODUCTION]: "Production",
  [JobCategory.SKILLED_TRADES]: "Skilled trades",
  [JobCategory.PROFESSIONAL]: "Professional",
};

export const jobHealthToBackgroundColor: Record<JobHealth, string> = {
  [JobHealth.PENDING]: "bg-slate-100",
  [JobHealth.ON_PACE]: "bg-emerald-100",
  [JobHealth.WARNING]: "bg-yellow-100",
  [JobHealth.AT_RISK]: "bg-red-100",
  [JobHealth.COMPLETED]: "bg-green-200",
};

export const jobHealthToDotColor: Record<JobHealth, string> = {
  [JobHealth.PENDING]: "bg-slate-400",
  [JobHealth.ON_PACE]: "bg-emerald-500",
  [JobHealth.WARNING]: "bg-amber-600",
  [JobHealth.AT_RISK]: "bg-rose-600",
  [JobHealth.COMPLETED]: "bg-emerald-700",
};

export const jobHealthToHoverBackgroundColor: Record<JobHealth, string> = {
  [JobHealth.PENDING]: "hover:bg-slate-100",
  [JobHealth.ON_PACE]: "hover:bg-emerald-100",
  [JobHealth.WARNING]: "hover:bg-yellow-100",
  [JobHealth.AT_RISK]: "hover:bg-red-100",
  [JobHealth.COMPLETED]: "hover:bg-green-200",
};

export const jobHealthToLabel: Record<JobHealth, string> = {
  [JobHealth.PENDING]: "Pending",
  [JobHealth.ON_PACE]: "On Pace",
  [JobHealth.WARNING]: "Warning",
  [JobHealth.AT_RISK]: "At Risk",
  [JobHealth.COMPLETED]: "Completed",
};

export const jobDifficultyToBgClasses: Record<JobDifficulty, string> = {
  [JobDifficulty.EASY]: "bg-emerald-100 hover:bg-emerald-100",
  [JobDifficulty.MODERATE]: "bg-yellow-100 hover:bg-yellow-100",
  [JobDifficulty.DIFFICULT]: "bg-orange-100 hover:bg-orange-100",
  [JobDifficulty.VERY_DIFFICULT]: "bg-red-100 hover:bg-red-100",
};

export const jobDifficultyToDotColor: Record<JobDifficulty, string> = {
  [JobDifficulty.EASY]: "bg-emerald-500",
  [JobDifficulty.MODERATE]: "bg-amber-500",
  [JobDifficulty.DIFFICULT]: "bg-orange-500",
  [JobDifficulty.VERY_DIFFICULT]: "bg-red-600",
};

export const jobDifficultyToLabel: Record<JobDifficulty, string> = {
  [JobDifficulty.EASY]: "Easy",
  [JobDifficulty.MODERATE]: "Moderate",
  [JobDifficulty.DIFFICULT]: "Difficult",
  [JobDifficulty.VERY_DIFFICULT]: "Very Difficult",
};

export const jobUrgencyToBgClasses: Record<JobUrgency, string> = {
  [JobUrgency.LOW]: "bg-emerald-100 hover:bg-emerald-100",
  [JobUrgency.MEDIUM]: "bg-yellow-100 hover:bg-yellow-100",
  [JobUrgency.HIGH]: "bg-orange-100 hover:bg-orange-100",
  [JobUrgency.URGENT]: "bg-red-100 hover:bg-red-100",
};

export const jobUrgencyToIcon: Record<JobUrgency, React.ElementType> = {
  [JobUrgency.LOW]: SignalLowIcon,
  [JobUrgency.MEDIUM]: SignalMediumIcon,
  [JobUrgency.HIGH]: SignalHighIcon,
  [JobUrgency.URGENT]: AlertCircleIcon,
};

export const jobUrgencyToLabel: Record<JobUrgency, string> = {
  [JobUrgency.LOW]: "Low",
  [JobUrgency.MEDIUM]: "Medium",
  [JobUrgency.HIGH]: "High",
  [JobUrgency.URGENT]: "Urgent",
};

export const jobStatusToBackgroundColor: Record<JobStatus, string> = {
  [JobStatus.ACTIVE]: "bg-green-100",
  [JobStatus.DRAFT]: "bg-slate-100",
  [JobStatus.CLOSED]: "bg-red-100",
};

export const jobStatusToDotColor: Record<JobStatus, string> = {
  [JobStatus.ACTIVE]: "bg-green-600",
  [JobStatus.DRAFT]: "bg-slate-400",
  [JobStatus.CLOSED]: "bg-red-600",
};

export const jobStatusToHoverBackgroundColor: Record<JobStatus, string> = {
  [JobStatus.ACTIVE]: "hover:bg-green-100",
  [JobStatus.DRAFT]: "hover:bg-slate-100",
  [JobStatus.CLOSED]: "hover:bg-red-100",
};

// * IMPORTANT: Important to keep these in the following order.
export const jobStatusToLabel: Record<JobStatus, string> = {
  [JobStatus.ACTIVE]: "Active",
  [JobStatus.DRAFT]: "Draft",
  [JobStatus.CLOSED]: "Closed",
};

// Mirrors guac's allowlist — the backend enforces this. The FE only
// uses it to hide the link/unlink controls. Intentionally the same five as
// JOB_PAUSE_AUTHORIZED_EMAILS below. Kept separate so the two permissions
// can diverge — collapse into one constant if they never do.
export const JOB_LINK_AUTHORIZED_EMAILS = [
  "corbin@laborup.com",
  "erika@laborup.com",
  "matt@laborup.com",
  "michael@laborup.com",
  "simba@laborup.com",
];

// Mirrors guac's allowlist — the backend enforces this. The FE only uses it
// to hide the "Add resumes" control on the Sourced tab while the flow is new.
export const SOURCING_RESUME_BATCH_AUTHORIZED_EMAILS = ["michael@laborup.com"];

export const PRODUCT_ALLOWED_EMAILS = [
  "tyler@laborup.com",
  "simba@laborup.com",
  "matt@laborup.com",
  "erika@laborup.com",
  "corin@laborup.com",
  "michael@laborup.com",
  "chitrang@laborup.com",
  "braden@laborup.com",
];

// Paused and covered are derived display states (status stays ACTIVE), so they
// get their own maps rather than JobStatus entries. The two behave identically
// everywhere — only the label and dot color differ.
export const jobPauseTypeToLabel: Record<JobPauseType, string> = {
  [JobPauseType.COVERED]: "Covered",
  [JobPauseType.PAUSED]: "Paused",
};

// What the status dropdown offers a staffer who can't pause directly, and what
// it reads once they have asked.
export const jobPauseTypeToRequestLabel: Record<JobPauseType, string> = {
  [JobPauseType.COVERED]: "Request covered",
  [JobPauseType.PAUSED]: "Request paused",
};

export const jobPauseTypeToRequestedLabel: Record<JobPauseType, string> = {
  [JobPauseType.COVERED]: "Cover requested",
  [JobPauseType.PAUSED]: "Pause requested",
};

export const jobPauseTypeToDotColor: Record<JobPauseType, string> = {
  [JobPauseType.COVERED]: "bg-blue-500",
  [JobPauseType.PAUSED]: "bg-amber-500",
};

export const jobPauseTypeToBackgroundColor: Record<JobPauseType, string> = {
  [JobPauseType.COVERED]: "bg-blue-100",
  [JobPauseType.PAUSED]: "bg-slate-200",
};

export const jobPauseTypeToHoverBackgroundColor: Record<JobPauseType, string> =
  {
    [JobPauseType.COVERED]: "hover:bg-blue-100",
    [JobPauseType.PAUSED]: "hover:bg-slate-200",
  };

// Mirrors guac. Whoever decides pause/cover requests. FE-only: it picks who
// polls for and sees the request cards, and only he is ever shown them.
export const JOB_PAUSE_REQUEST_APPROVER_EMAIL = "corbin@laborup.com";

// Mirrors guac's allowlist. The BE enforces this. The FE only uses it to decide
// who pauses directly and who has to request it — marking a job active is open
// to everyone.
export const JOB_PAUSE_AUTHORIZED_EMAILS = [
  "corbin@laborup.com",
  "erika@laborup.com",
  "matt@laborup.com",
  "michael@laborup.com",
  "simba@laborup.com",
];

export const laborupProRankToColor: Record<LaborupProRank, string> = {
  [LaborupProRank.BRONZE]: "#A77044",
  [LaborupProRank.SILVER]: "#A7A7AD",
  [LaborupProRank.GOLD]: "#D6AF36",
};

export const laborupProRankToLabel: Record<LaborupProRank, string> = {
  [LaborupProRank.BRONZE]: "Bronze",
  [LaborupProRank.SILVER]: "Silver",
  [LaborupProRank.GOLD]: "Gold",
};

export const overtimeAvailabilityToLabel: Record<OvertimeAvailability, string> =
  {
    [OvertimeAvailability.REQUIRED]: "Required",
    [OvertimeAvailability.OPTIONAL]: "Optional",
    [OvertimeAvailability.SEASONAL]: "Seasonal",
    [OvertimeAvailability.RARE]: "Rare",
    [OvertimeAvailability.UNLIMITED]: "Unlimited",
  };

export const paymentTermsToLabel: Record<PaymentTerms, string> = {
  [PaymentTerms.NET15]: "Net 15",
  [PaymentTerms.NET30]: "Net 30",
  [PaymentTerms.NET60]: "Net 60",
};

export const dateFilterToLabel: Record<DateFilterOption, string> = {
  [DateFilterOption.Today]: "Today",
  [DateFilterOption.Yesterday]: "Yesterday",
  [DateFilterOption.LastWeek]: "Last Week",
  [DateFilterOption.ThisWeek]: "This Week",
  [DateFilterOption.LastMonth]: "Last Month",
  [DateFilterOption.ThisMonth]: "This Month",
  [DateFilterOption.LastQuarter]: "Last Quarter",
  [DateFilterOption.ThisQuarter]: "This Quarter",
  [DateFilterOption.ThisYear]: "This Year",
  [DateFilterOption.AllTime]: "All Time",
  [DateFilterOption.Custom]: "Custom",
};

export const placementStatusToBackgroundColor: Record<PlacementStatus, string> =
  {
    [PlacementStatus.OFFERED]: "bg-yellow-200",
    [PlacementStatus.ACCEPTED]: "bg-green-100",
    [PlacementStatus.REJECTED]: "bg-red-200",
    [PlacementStatus.REVOKED]: "bg-red-200",
    [PlacementStatus.EMPLOYED]: "bg-green-300",
    [PlacementStatus.COMPLETED]: "bg-blue-200",
  };

export const placementStatusToDotColor: Record<PlacementStatus, string> = {
  [PlacementStatus.OFFERED]: "bg-yellow-400",
  [PlacementStatus.ACCEPTED]: "bg-green-300",
  [PlacementStatus.REJECTED]: "bg-red-400",
  [PlacementStatus.REVOKED]: "bg-red-400",
  [PlacementStatus.EMPLOYED]: "bg-green-400",
  [PlacementStatus.COMPLETED]: "bg-blue-400",
};

export const placementStatusToHoverBackgroundColor: Record<
  PlacementStatus,
  string
> = {
  [PlacementStatus.OFFERED]: "hover:bg-yellow-200",
  [PlacementStatus.ACCEPTED]: "hover:bg-green-100",
  [PlacementStatus.REJECTED]: "hover:bg-red-200",
  [PlacementStatus.REVOKED]: "hover:bg-red-200",
  [PlacementStatus.EMPLOYED]: "hover:bg-green-300",
  [PlacementStatus.COMPLETED]: "hover:bg-blue-200",
};

export const placementStatusToLabel: Record<PlacementStatus, string> = {
  [PlacementStatus.OFFERED]: "Offered",
  [PlacementStatus.ACCEPTED]: "Accepted",
  [PlacementStatus.REJECTED]: "Rejected",
  [PlacementStatus.REVOKED]: "Revoked",
  [PlacementStatus.EMPLOYED]: "Employed",
  [PlacementStatus.COMPLETED]: "Completed",
};

export const positionCategoryToLabel: Record<PositionCategory, string> = {
  [PositionCategory.PRODUCTION]: "Production",
  [PositionCategory.MACHINING]: "Machining",
  [PositionCategory.WELDING]: "Welding",
  [PositionCategory.MAINTENANCE]: "Maintenance",
  [PositionCategory.ENGINEERING]: "Engineering",
};

export const quoMessageStatusToLabel: Record<QuoMessageStatus, string> = {
  [QuoMessageStatus.QUEUED]: "Queued",
  [QuoMessageStatus.SENT]: "Sent",
  [QuoMessageStatus.DELIVERED]: "Delivered",
  [QuoMessageStatus.UNDELIVERED]: "Undelivered",
};

export const requisitionStatusToBackgroundColor: Record<
  RequisitionStatus,
  string
> = {
  [RequisitionStatus.PENDING]: "bg-gray-200",
  [RequisitionStatus.LIVE]: "bg-sky-200",
  [RequisitionStatus.BACKFILL]: "bg-purple-200",
  [RequisitionStatus.FILLED]: "bg-green-200",
  [RequisitionStatus.WASH]: "bg-orange-200",
  [RequisitionStatus.LOST]: "bg-red-200",
  [RequisitionStatus.COMPLETED]: "bg-green-200",
};

export const requisitionStatusToDotColor: Record<RequisitionStatus, string> = {
  [RequisitionStatus.PENDING]: "bg-gray-300",
  [RequisitionStatus.LIVE]: "bg-sky-300",
  [RequisitionStatus.BACKFILL]: "bg-purple-300",
  [RequisitionStatus.FILLED]: "bg-green-300",
  [RequisitionStatus.WASH]: "bg-orange-300",
  [RequisitionStatus.LOST]: "bg-red-300",
  [RequisitionStatus.COMPLETED]: "bg-green-300",
};

export const requisitionStatusToFirstLetter: Record<RequisitionStatus, string> =
  {
    [RequisitionStatus.PENDING]: "P",
    [RequisitionStatus.LIVE]: "L",
    [RequisitionStatus.BACKFILL]: "B",
    [RequisitionStatus.FILLED]: "F",
    [RequisitionStatus.WASH]: "W",
    [RequisitionStatus.LOST]: "O",
    [RequisitionStatus.COMPLETED]: "C",
  };

export const requisitionStatusToHoverBackgroundColor: Record<
  RequisitionStatus,
  string
> = {
  [RequisitionStatus.PENDING]: "hover:bg-gray-200",
  [RequisitionStatus.LIVE]: "hover:bg-sky-200",
  [RequisitionStatus.BACKFILL]: "hover:bg-purple-200",
  [RequisitionStatus.FILLED]: "hover:bg-green-200",
  [RequisitionStatus.WASH]: "hover:bg-orange-200",
  [RequisitionStatus.LOST]: "hover:bg-red-200",
  [RequisitionStatus.COMPLETED]: "hover:bg-green-200",
};

export const requisitionStatusToLabel: Record<RequisitionStatus, string> = {
  [RequisitionStatus.PENDING]: "Pending",
  [RequisitionStatus.LIVE]: "Live",
  [RequisitionStatus.BACKFILL]: "Backfill",
  [RequisitionStatus.FILLED]: "Filled",
  [RequisitionStatus.WASH]: "Wash",
  [RequisitionStatus.LOST]: "Lost",
  [RequisitionStatus.COMPLETED]: "Completed",
};

export const revenueTypeToLabel: Record<RevenueType, string> = {
  [RevenueType.FIXED]: "Fixed",
  [RevenueType.PERCENT]: "Percent",
};

export const shiftTypeToLabel: Record<ShiftType, string> = {
  [ShiftType.FIRSTSHIFT]: "First shift",
  [ShiftType.SECONDSHIFT]: "Second shift",
  [ShiftType.THIRDSHIFT]: "Third shift",
  [ShiftType.WEEKEND]: "Weekend shift",
};

export const stakeholderRoleToLabel: Record<StakeholderRole, string> = {
  [StakeholderRole.BUYER]: "Buyer",
  [StakeholderRole.FINAL_DECISION_MAKER]: "Final Decision Maker",
  [StakeholderRole.HIRING_MANAGER]: "Hiring Manager",
  [StakeholderRole.INFLUENCER]: "Influencer",
  [StakeholderRole.INTERVIEWER]: "Interviewer",
  [StakeholderRole.PRIMARY_CONTACT]: "Primary Contact",
};

export const stakeholderValuePerceptionToLabel: Record<
  StakeholderValuePerception,
  string
> = {
  [StakeholderValuePerception.SPEED_COMMUNICATION]: "Speed (communication)",
  [StakeholderValuePerception.SPEED_SUBMITTALS]: "Speed (submittals)",
  [StakeholderValuePerception.TRANSPARENCY]: "Transparency",
  [StakeholderValuePerception.QUALITY_SUBMITTALS]: "Quality (submittals)",
  [StakeholderValuePerception.RELATIONSHIP_AND_TRUST]: "Relationship and Trust",
  [StakeholderValuePerception.COST]: "Cost",
  [StakeholderValuePerception.RETENTION]: "Retention",
};

export const taskPriorityToLabel: Record<TaskPriority, string> = {
  [TaskPriority.NONE]: "None",
  [TaskPriority.LOW]: "Low",
  [TaskPriority.MEDIUM]: "Medium",
  [TaskPriority.HIGH]: "High",
  [TaskPriority.URGENT]: "Urgent",
};

export const textAIOutreachStatusToLabel: Record<TextAIOutreachStatus, string> =
  {
    [TextAIOutreachStatus.INITIATED]: "Initiated",
    [TextAIOutreachStatus.ACTIVE]: "Active",
    [TextAIOutreachStatus.CALL_SCHEDULED]: "Call Scheduled",
    [TextAIOutreachStatus.CALL_RESCHEDULED]: "Call Rescheduled",
    [TextAIOutreachStatus.CALL_COMPLETED]: "Call Completed",
    [TextAIOutreachStatus.CALL_CANCELLED]: "Call Cancelled",
    [TextAIOutreachStatus.CLOSED]: "Closed",
    [TextAIOutreachStatus.HOLD]: "On Hold",
    [TextAIOutreachStatus.STOP]: "Stopped",
    [TextAIOutreachStatus.PROXY]: "Proxy",
    [TextAIOutreachStatus.HANDOFF]: "Handoff",
  };

export const interviewDurationToLabel: Record<InterviewDuration, string> = {
  [InterviewDuration.MINUTES_15]: "15 min",
  [InterviewDuration.MINUTES_30]: "30 min",
  [InterviewDuration.MINUTES_45]: "45 min",
  [InterviewDuration.MINUTES_60]: "1 hr",
  [InterviewDuration.MINUTES_75]: "1 hr 15 min",
  [InterviewDuration.MINUTES_90]: "1 hr 30 min",
  [InterviewDuration.MINUTES_120]: "2 hr",
  [InterviewDuration.MINUTES_150]: "2 hr 30 min",
  [InterviewDuration.MINUTES_180]: "3 hr",
};

export const interviewStatusToLabel: Record<InterviewStatus, string> = {
  [InterviewStatus.SCHEDULED]: "Scheduled",
  [InterviewStatus.CANDIDATE_CONFIRMED]: "Candidate Confirmed",
  [InterviewStatus.COMPLETED]: "Completed",
  [InterviewStatus.CANDIDATE_NO_SHOW]: "Candidate No-Show",
  [InterviewStatus.CANDIDATE_CANCELED]: "Candidate Canceled",
  [InterviewStatus.CUSTOMER_CANCELED]: "Customer Canceled",
};

export const interviewStatusToBackgroundColor: Record<InterviewStatus, string> =
  {
    [InterviewStatus.SCHEDULED]: "bg-blue-500",
    [InterviewStatus.CANDIDATE_CONFIRMED]: "bg-green-400",
    [InterviewStatus.COMPLETED]: "bg-green-600",
    [InterviewStatus.CANDIDATE_NO_SHOW]: "bg-red-500",
    [InterviewStatus.CANDIDATE_CANCELED]: "bg-gray-500",
    [InterviewStatus.CUSTOMER_CANCELED]: "bg-gray-500",
  };

export const timezoneToIana: Record<Timezone, string> = {
  [Timezone.EASTERN]: "America/New_York",
  [Timezone.CENTRAL]: "America/Chicago",
  [Timezone.MOUNTAIN]: "America/Denver",
  [Timezone.MOUNTAIN_ARIZONA]: "America/Phoenix",
  [Timezone.PACIFIC]: "America/Los_Angeles",
};

export const timezoneToLabel: Record<Timezone, string> = {
  [Timezone.EASTERN]: "Eastern (ET)",
  [Timezone.CENTRAL]: "Central (CT)",
  [Timezone.MOUNTAIN]: "Mountain (MT)",
  [Timezone.MOUNTAIN_ARIZONA]: "Mountain - Arizona (MST)", // No DST!
  [Timezone.PACIFIC]: "Pacific (PT)",
};

export const voiceAIOutreachStatusToLabel: Record<
  VoiceAIOutreachStatus,
  string
> = {
  [VoiceAIOutreachStatus.CANCELLED]: "Canceled",
  [VoiceAIOutreachStatus.COMPLETED]: "Completed",
  [VoiceAIOutreachStatus.FAILED]: "Failed",
  [VoiceAIOutreachStatus.IN_PROGRESS]: "In progress",
  [VoiceAIOutreachStatus.PENDING]: "Pending",
  [VoiceAIOutreachStatus.VOICEMAIL]: "Voicemail",
};

export const positionCategoryOptions = Object.entries(
  positionCategoryToLabel,
).map((entry) => ({
  label: entry[1],
  value: entry[0] as PositionCategory,
}));

// Canonical desired positions — the backend maps each to a PositionCategory
// bucket, so user desired positions must stay within this list.
export const coreDesiredPositions = [
  "Production technician",
  "Machinist",
  "Welder",
  "Maintenance technician",
  "Engineer",
];

export const OTHER_DESIRED_POSITION = "Other";

export const desiredPositionOptions = [
  ...coreDesiredPositions,
  OTHER_DESIRED_POSITION,
].map((position) => ({
  label: position,
  value: position,
}));

// DEFAULTS //

export const defaultJobQueryParams: JobQueryParams = {
  bottlenecks: [],
  difficulties: [],
  healths: [],
  jobStatuses: [],
  order: SortOrder.Asc,
  ownerIds: [],
  requisitionStatuses: [],
  sort: JobTrackerSort.CreatedAt,
  urgencies: [],
};

export const defaultJobsSortKeyAndOrder = JobsSortKeyAndOrder.CreatedAtDesc;

// Pipeline sort keys whose first click should lead Asc (A→Z for text, shortest→longest for timeInStage).
// All other keys lead Desc on first click.
export const jobPipelineAscLeadingSortKeys: JobPipelineSortKey[] = [
  JobPipelineSortKey.Name,
  JobPipelineSortKey.Staffer,
  JobPipelineSortKey.TimeInStage,
  JobPipelineSortKey.Location,
];

// Pipeline freshness tints: warm at 3 days, red at 6 days.
export const FRESHNESS_WARN_AFTER_HOURS = 72;
export const FRESHNESS_STALE_AFTER_HOURS = 144;

export const SECONDS_PER_MINUTE = 60;

// Tag every search-v2 analytics event so it can be segmented from legacy search.
export const SEARCH_V2_ANALYTICS_METADATA = { search_version: "v2" };

// One page of search results. Shared by regular search and Sally mode so the
// two corpora scroll identically.
export const SEARCH_V2_PAGE_SIZE = 25;

export const MAX_SEARCH_V2_KEYWORDS = 10;

export const MAX_JOB_KEYWORDS = 10;

export const MAX_DEFAULT_SEARCH_KEYWORDS = 3;

export const profileHighlightGroups = [
  { className: "border-blue-100 bg-blue-50 text-blue-700", type: "Skills" },
  {
    className: "border-violet-100 bg-violet-50 text-violet-700",
    type: "Equipment",
  },
  {
    className: "border-teal-100 bg-teal-50 text-teal-700",
    type: "Processes",
  },
  {
    className: "border-orange-100 bg-orange-50 text-orange-700",
    type: "Industries",
  },
  {
    className: "border-green-100 bg-green-50 text-green-700",
    type: "Certifications",
  },
  {
    className: "border-slate-200 bg-slate-100 text-slate-700",
    type: "Soft Skills",
  },
  { className: "border-red-100 bg-red-50 text-red-600", type: "Safety" },
] as const;

export const DEFAULT_SEARCH_WEIGHTS: SearchV2Weights = {
  activityWeight: 0,
  semanticWeight: 0.5,
  textWeight: 0.5,
  weRecencyWeight: 0.25,
};

// Commute slider tops out at 90m. Slid fully right = no cap, so anyone beyond
// 90m still shows; any nudge left caps the commute.
export const MAX_COMMUTE_FILTER_MINUTES = 90;

export const defaultPipelineFilterAndSortState: PipelineFilterAndSortState = {
  includeCouldNotContact: false,
  includeEmploymentEnded: false,
  includeRejected: false,
  locations: [],
  name: "",
  sortKeyAndOrder: {
    key: JobPipelineSortKey.AgenticScore,
    direction: SortOrder.Desc,
  },
  sources: [],
  excellentScore: false,
  greatScore: false,
  onlyCommsToSend: false,
  stevieCallCompleted: false,
  maxCommuteSeconds: null,
};

// Sort options shown in the kanban view's "Sort by" dropdown. The dropdown
// is a curated subset of the full sort key space — the table view exposes
// every JobPipelineSortKey via its column headers, but the kanban needs a
// fixed list since there are no column headers to click. State is shared
// with the table: selecting an option here also drives the table's sort
// when the user switches views.
export const pipelineKanbanSortOptions: {
  label: string;
  key: JobPipelineSortKey;
  direction: SortOrder;
}[] = [
  {
    label: "Agentic score (high to low)",
    key: JobPipelineSortKey.AgenticScore,
    direction: SortOrder.Desc,
  },
  {
    label: "Distance (near to far)",
    key: JobPipelineSortKey.Location,
    direction: SortOrder.Asc,
  },
  {
    label: "Last Ping (newest to oldest)",
    key: JobPipelineSortKey.LastPing,
    direction: SortOrder.Desc,
  },
  {
    label: "Last Ping (oldest to newest)",
    key: JobPipelineSortKey.LastPing,
    direction: SortOrder.Asc,
  },
  {
    label: "Time in Stage (newest to oldest)",
    key: JobPipelineSortKey.TimeInStage,
    direction: SortOrder.Asc,
  },
  {
    label: "Time in Stage (oldest to newest)",
    key: JobPipelineSortKey.TimeInStage,
    direction: SortOrder.Desc,
  },
];

export const defaultPipelineKanbanSortLabel = "Agentic score (high to low)";

export const taskPriorityToIcon: Record<TaskPriority, React.ElementType> = {
  [TaskPriority.NONE]: SignalHighIcon, // This will be combined with low opacity to represent no priority
  [TaskPriority.LOW]: SignalLowIcon,
  [TaskPriority.MEDIUM]: SignalMediumIcon,
  [TaskPriority.HIGH]: SignalHighIcon,
  [TaskPriority.URGENT]: AlertCircleIcon,
};

// OPTIONS //

export const candidacySourceOptions = Object.entries(
  candidacySourceToLabel,
).map((entry) => ({
  label: entry[1],
  value: entry[0] as CandidacySource,
}));

export const candidacyStatusOptions = Object.entries(
  candidacyStatusToLabel,
).map((entry) => ({
  label: entry[1],
  value: entry[0] as CandidacyStatus,
}));

export const communicationFrequencyOptions: {
  value: CommunicationFrequency;
  label: string;
}[] = [
  { value: CommunicationFrequency.DAILY, label: "Daily" },
  { value: CommunicationFrequency.FEW_WEEKLY, label: "A couple weekly" },
  { value: CommunicationFrequency.WEEKLY, label: "Weekly" },
  { value: CommunicationFrequency.MAJOR_UPDATES, label: "Major updates only" },
  {
    value: CommunicationFrequency.ONLY_ON_ESCALATION,
    label: "Only when needing to escalate",
  },
];

export const communicationModeOptions: {
  value: CommunicationMode;
  label: string;
}[] = [
  { value: CommunicationMode.TEXT, label: "Text" },
  { value: CommunicationMode.EMAIL, label: "Email" },
  { value: CommunicationMode.CALL, label: "Call" },
  { value: CommunicationMode.VIDEO, label: "Video" },
];

export const companyIndustryOptions = Object.entries(
  companyIndustryToLabel,
).map((entry) => ({ value: entry[0] as CompanyIndustry, label: entry[1] }));

export const businessTrendToLabel: Record<BusinessTrend, string> = {
  [BusinessTrend.GROWING]: "Growing",
  [BusinessTrend.STABLE]: "Stable",
  [BusinessTrend.DECLINING]: "Declining",
};

export const businessTrendOptions = Object.entries(businessTrendToLabel).map(
  (entry) => ({ value: entry[0] as BusinessTrend, label: entry[1] }),
);

export const staffingFirmCostStructureToLabel: Record<
  StaffingFirmCostStructure,
  string
> = {
  [StaffingFirmCostStructure.FIXED]: "Fixed",
  [StaffingFirmCostStructure.PERCENTAGE]: "Percentage",
};

export const staffingFirmCostStructureOptions = Object.entries(
  staffingFirmCostStructureToLabel,
).map((entry) => ({
  value: entry[0] as StaffingFirmCostStructure,
  label: entry[1],
}));

export const compensationTypeOptions = Object.entries(
  compensationTypeToLabel,
).map((entry) => ({
  label: entry[1],
  value: entry[0] as CompensationType,
}));

export const competencyRatingTypeOptions = [
  { label: "Star Rating (1-5)", value: "rating" },
  { label: "Yes/No", value: "binary" },
  { label: "Completed/Not Completed", value: "completion" },
];

export const demoPositionOptions = [
  {
    label: "Laminator",
    value: DemoPosition.LAMINATOR,
  },
  {
    label: "Machinist",
    value: DemoPosition.MACHINIST,
  },
  {
    label: "Maintenance",
    value: DemoPosition.MAINTENANCE,
  },
  {
    label: "Welder",
    value: DemoPosition.WELDER,
  },
];

export const demoRegionOptions = [
  {
    label: "Birmingham, AL",
    value: DemoRegion.BIRMINGHAM_AL,
  },
  {
    label: "Bowling Green, KY",
    value: DemoRegion.BOWLING_GREEN_KY,
  },
  {
    label: "Chattanooga, TN",
    value: DemoRegion.CHATTANOOGA_TN,
  },
  {
    label: "Cincinnati, OH",
    value: DemoRegion.CINCINNATI_OH,
  },
  {
    label: "Clarksville, TN",
    value: DemoRegion.CLARKSVILLE_TN,
  },
  {
    label: "Cleveland, OH",
    value: DemoRegion.CLEVELAND_OH,
  },
  {
    label: "Columbus, OH",
    value: DemoRegion.COLUMBUS_OH,
  },
  {
    label: "Dayton, OH",
    value: DemoRegion.DAYTON_OH,
  },
  {
    label: "Huntsville, AL",
    value: DemoRegion.HUNTSVILLE_AL,
  },
  {
    label: "Knoxville, TN",
    value: DemoRegion.KNOXVILLE_TN,
  },
  {
    label: "Lexington, KY",
    value: DemoRegion.LEXINGTON_KY,
  },
  {
    label: "Los Angeles, CA",
    value: DemoRegion.LOS_ANGELES_CA,
  },
  {
    label: "Louisville, KY",
    value: DemoRegion.LOUISVILLE_KY,
  },
  {
    label: "Mobile, AL",
    value: DemoRegion.MOBILE_AL,
  },
  {
    label: "Nashville, TN",
    value: DemoRegion.NASHVILLE_TN,
  },
];

export const employmentTypeOptions = Object.entries(employmentTypeToLabel).map(
  (entry) => ({
    label: entry[1],
    value: entry[0] as EmploymentType,
  }),
);

export const evaluationBlockTypeOptions = [
  { label: "Open-ended", value: "open-ended" },
  { label: "Structured", value: "structured" },
];

export const interviewDurationOptions = Object.entries(
  interviewDurationToLabel,
).map((entry) => ({
  label: entry[1],
  value: entry[0] as InterviewDuration,
}));

export const interviewStatusOptions = Object.entries(
  interviewStatusToLabel,
).map((entry) => ({
  label: entry[1],
  value: entry[0] as InterviewStatus,
}));

export const jobBottleneckOptions = Object.entries(jobBottleneckToLabel).map(
  (entry) => ({
    label: entry[1],
    value: entry[0] as JobBottleneck,
  }),
);

export const jobCategoryOptions = Object.entries(jobCategoryToLabel).map(
  (entry) => ({
    label: entry[1],
    value: entry[0] as JobCategory,
  }),
);

// The APR ceiling for Stevie outreach: candidates rated above the selected
// value are left to a human recruiter. Mirrors guac's
// STEVIE_OUTREACH_MAX_RATING_DEFAULT of 3, which skips 4-5 star candidates.
export const MAX_APR_RATING = 5;

export const stevieOutreachMaxRatingOptions = Array.from(
  { length: MAX_APR_RATING },
  (_, index) => {
    const value = index + 1;
    const stars = `${String(value)} star${value === 1 ? "" : "s"} and below`;
    return {
      label: value === MAX_APR_RATING ? `${stars} (all candidates)` : stars,
      value,
    };
  },
);

export const jobDifficultyOptions = Object.entries(jobDifficultyToLabel).map(
  (entry) => ({
    label: entry[1],
    value: entry[0] as JobDifficulty,
  }),
);

export const jobHealthOptions = Object.entries(jobHealthToLabel).map(
  ([value, label]) => ({
    label,
    value: value as JobHealth,
  }),
);

export const jobStatusOptions = Object.entries(jobStatusToLabel).map(
  (entry) => ({
    label: entry[1],
    value: entry[0] as JobStatus,
  }),
);
export const jobStatusOptionsForCreate = jobStatusOptions.filter(
  (option) => option.value !== JobStatus.CLOSED,
);

export const jobUrgencyOptions = Object.entries(jobUrgencyToLabel).map(
  (entry) => ({
    label: entry[1],
    value: entry[0] as JobUrgency,
  }),
);

export const laborupProRankOptions = Object.entries(laborupProRankToLabel).map(
  (entry) => ({
    label: entry[1],
    value: entry[0] as LaborupProRank,
  }),
);

export const overtimeAvailabilityOptions = Object.entries(
  overtimeAvailabilityToLabel,
).map((entry) => ({
  label: entry[1],
  value: entry[0] as OvertimeAvailability,
}));

export const paymentTermsOptions = Object.entries(paymentTermsToLabel).map(
  (entry) => ({
    label: entry[1],
    value: entry[0] as PaymentTerms,
  }),
);

export const dateFilterOptions = Object.entries(dateFilterToLabel)
  .map((entry) => ({
    label: entry[1],
    value: entry[0] as DateFilterOption,
  }))
  .filter((entry) => entry.value !== DateFilterOption.Custom);

export const placementStatusOptions = Object.entries(
  placementStatusToLabel,
).map((entry) => ({
  label: entry[1],
  value: entry[0] as PlacementStatus,
}));

export const myCandidaciesSortOptions = [
  { label: "Last Ping", value: MyCandidaciesSortBy.LastPingAt },
  { label: "Stage", value: MyCandidaciesSortBy.Status },
  { label: "Agent Score", value: MyCandidaciesSortBy.AgentScore },
];

export const myCandidaciesStatusOptions = candidacyStatusOptions.filter(
  (option) =>
    ![
      CandidacyStatus.CLOSED,
      CandidacyStatus.PLACED,
      CandidacyStatus.EMPLOYED,
      CandidacyStatus.EMPLOYMENT_ENDED,
      CandidacyStatus.REJECTED,
    ].includes(option.value),
);

export const placementTrackerSortOptions = [
  { label: "Created At", value: PlacementTrackerSortBy.CreatedAt },
  { label: "Date Offered", value: PlacementTrackerSortBy.DateOffered },
  { label: "Date Accepted", value: PlacementTrackerSortBy.DateAccepted },
  { label: "Revenue Earned", value: PlacementTrackerSortBy.RevenueEarned },
];

export const requisitionStatusOptions = Object.entries(
  requisitionStatusToLabel,
).map((entry) => ({
  label: entry[1],
  value: entry[0] as RequisitionStatus,
}));

export const revenueTypeOptions = Object.entries(revenueTypeToLabel).map(
  (entry) => ({
    label: entry[1],
    value: entry[0] as RevenueType,
  }),
);

export const shiftTypeOptions = Object.entries(shiftTypeToLabel).map(
  (entry) => ({
    label: entry[1],
    value: entry[0] as ShiftType,
  }),
);

export const sourceOptions = sources.map((source) => ({
  label: source,
  value: source,
}));

export const stafferSelectableCandidacySourceOptions =
  candidacySourceOptions.filter((option) =>
    stafferSelectableCandidacySources.includes(option.value),
  );

export const stakeholderRoleOptions = Object.entries(
  stakeholderRoleToLabel,
).map((entry) => ({ value: entry[0] as StakeholderRole, label: entry[1] }));

export const stakeholderValuePerceptionOptions: {
  value: StakeholderValuePerception;
  label: string;
}[] = Object.entries(stakeholderValuePerceptionToLabel).map((entry) => ({
  value: entry[0] as StakeholderValuePerception,
  label: entry[1],
}));

export const taskPriorityOptions: {
  label: string;
  value: TaskPriority;
}[] = Object.entries(taskPriorityToLabel).map((entry) => ({
  label: entry[1],
  value: entry[0] as TaskPriority,
}));

export const taskStatusOptions = [
  { label: "Completed", value: TaskStatus.COMPLETED },
  { label: "Incomplete", value: TaskStatus.INCOMPLETE },
  { label: "All", value: TaskStatus.ALL },
];

export const textAIOutreachStatusOptions = Object.entries(
  textAIOutreachStatusToLabel,
).map((entry) => ({
  label: entry[1],
  value: entry[0] as TextAIOutreachStatus,
}));

export const timezoneOptions = Object.entries(timezoneToLabel).map((entry) => ({
  label: entry[1],
  value: entry[0] as Timezone,
}));

export const voiceAIOutreachStatusOptions = Object.entries(
  voiceAIOutreachStatusToLabel,
).map((entry) => ({
  label: entry[1],
  value: entry[0] as VoiceAIOutreachStatus,
}));

export const defaultLeaderboardQueryParams: LeaderboardQueryParams = {
  order: SortOrder.Desc,
  sort: LeaderboardSort.Revenue,
  timePeriod: DateFilterOption.ThisWeek,
};

export const defaultMyCandidaciesQueryParams: MyCandidaciesQueryParams = {
  order: SortOrder.Desc,
  sort: MyCandidaciesSortBy.LastPingAt,
  statuses: [],
};

export const defaultPlacementQueryParams: PlacementQueryParams = {
  dateAcceptedFilter: DateFilterOption.AllTime,
  order: SortOrder.Desc,
  ownerIds: [],
  sort: PlacementTrackerSortBy.CreatedAt,
  placementStatuses: [],
};

export const defaultInterviewQueryParams: InterviewQueryParams = {
  order: SortOrder.Asc,
  sort: InterviewSort.DateTime,
};

export const defaultRequisitionQueryParams: RequisitionQueryParams = {
  order: SortOrder.Asc,
  sort: RequisitionSort.CreatedAt,
  staffers: [],
  statuses: [],
};
// Adding this as a constant because order matters in the query params. Important for keeping the cache in sync.
export const defaultRequisitionStatusesForOffers: RequisitionStatus[] = [
  RequisitionStatus.BACKFILL,
  RequisitionStatus.LIVE,
];

export const defaultTaskQueryParams: TaskQueryParams = {
  assignees: [],
  order: SortOrder.Asc,
  priority: [],
  sort: TaskSort.CreatedAt,
  status: TaskStatus.INCOMPLETE,
};

// Shared copy for the irreversible "take over from the agent" confirmation,
// reused by both the explicit Take over button and the send-while-active guard
// in the message and email threads.
export const STAFFER_AGENT_TAKEOVER_DIALOG = {
  title: "Take over this conversation?",
  description:
    "This action will permanently disable the agent for this candidate. You cannot re-enable it afterward, and you'll be responsible for all wrangling going forward.",
  confirmButtonText: "Take over",
};

// Per-metric hex-badge gradients, shared by the leaderboard top-performer cards,
// the racetrack hero, and the leader badges next to staffer names.
export const REVENUE_GRADIENT: HexGradient = {
  top: "#bbf7d0",
  mid: "#4ade80",
  bottom: "#16a34a",
};
export const PLACEMENTS_GRADIENT: HexGradient = {
  top: "#fde68a",
  mid: "#fbbf24",
  bottom: "#d97706",
};
export const SCREENS_GRADIENT: HexGradient = {
  top: "#bae6fd",
  mid: "#38bdf8",
  bottom: "#0284c7",
};
export const SUBMITTALS_GRADIENT: HexGradient = {
  top: "#ddd6fe",
  mid: "#a78bfa",
  bottom: "#7c3aed",
};
export const INTERVIEWS_GRADIENT: HexGradient = {
  top: "#a7f3d0",
  mid: "#34d399",
  bottom: "#059669",
};

export const JOB_FORM_INPUT_CLASS_NAME = "bg-slate-50 focus:bg-white";
export const JOB_FORM_SELECT_CLASS_NAME = "bg-slate-50";
export const JOB_FORM_RICH_TEXT_CLASS_NAME =
  "bg-slate-50 focus-within:bg-white";

// Option ids below mirror angkor / guac searchTerms encodings exactly.
export const sourcingRadiusOptions = [
  { label: "10 mi", value: "10" },
  { label: "25 mi", value: "25" },
  { label: "50 mi", value: "50" },
  { label: "75 mi", value: "75" },
  { label: "100 mi", value: "100" },
];

export const resumeLastUpdatedOptions = [
  { label: "Within last day", value: "1d" },
  { label: "Within last 3 days", value: "3d" },
  { label: "Within last week", value: "1w" },
  { label: "Within last month", value: "1mo" },
  { label: "Within last 3 months", value: "3mo" },
  { label: "Within last 6 months", value: "6mo" },
  { label: "Show all", value: "all" },
];

export const candidateLastActiveOptions = [
  { label: "Within last week", value: "1w" },
  { label: "Within last month", value: "1mo" },
  { label: "Within last 3 months", value: "3mo" },
  { label: "Within last 6 months", value: "6mo" },
  { label: "Show all", value: "all" },
];

export const excludeContactedOptions = [
  { label: "Within last 30 days", value: "30d" },
  { label: "Within last 60 days", value: "60d" },
  { label: "Within last 90 days", value: "90d" },
  { label: "Show all", value: "all" },
];

export const timeAtCurrentJobOptions = [
  { label: "Greater than 3 months", value: "3mo" },
  { label: "Greater than 1 year", value: "1y" },
  { label: "Greater than 2 years", value: "2y" },
  { label: "Greater than 5 years", value: "5y" },
  { label: "Greater than 10 years", value: "10y" },
  { label: "Show all", value: "all" },
];

// Minimum agentic score (stars) for outreach — only 3, 4, 5 are valid.
export const MIN_SOURCING_SCORE = 3;
export const MAX_SOURCING_SCORE = 5;

export const DEFAULT_SOURCING_RADIUS_MILES = "25";
export const DEFAULT_SOURCING_MAX_MESSAGES = 200;
export const DEFAULT_SOURCING_MINIMUM_SCORE = 4;

// Shown when a job has no external sourcing request (Sourced tab + read-only config).
export const EXTERNAL_SOURCING_NO_TICKET_MESSAGE =
  "External sourcing isn't occurring for this job. Talk to Moo Deng or Corbin to request it.";

// The resume databases a staffer can drop resumes from, as they should read in
// the picker.
export const EXTERNAL_RESUME_SOURCE_LABELS: Record<
  ExternalResumeSource,
  string
> = {
  [ExternalResumeSource.JUICEBOX_RESUME_UNLOCK]: "Juicebox Resume Unlock",
  [ExternalResumeSource.MONSTER_RESUME_UNLOCK]: "Monster Resume Unlock",
  [ExternalResumeSource.ZIPRECRUITER_RESUME_UNLOCK]:
    "ZipRecruiter Resume Unlock",
};

// Shown when a job has no ZipRecruiter sourcing request (Sourced tab).
export const ZIP_RECRUITER_SOURCING_NO_TICKET_MESSAGE =
  "ZipRecruiter sourcing isn't occurring for this job. Talk to Moo Deng or Corbin to request it.";

// Per-bucket label, pill styling, hover styling, and icon for the agentic score
// chip + dropdown. hoverClassName is applied only to the clickable dropdown option
// buttons (className is also used by the read-only chip + corner badge).
export const agenticScoreBucketConfig: Record<
  AgenticScoreBucket,
  { label: string; className: string; hoverClassName: string; Icon: LucideIcon }
> = {
  [AgenticScoreBucket.EXCELLENT]: {
    label: "Excellent",
    className:
      "border-indigo-200/80 bg-indigo-100 text-indigo-800 dark:border-[#6F8FAA] dark:bg-[#27435E] dark:text-[#C6D6E2]",
    hoverClassName: "hover:bg-indigo-200 dark:hover:bg-[#355477]",
    Icon: StarIcon,
  },
  [AgenticScoreBucket.GREAT]: {
    label: "Great",
    className:
      "bg-emerald-500 text-white dark:bg-[#315F34] dark:text-[#DCEBDA]",
    hoverClassName: "hover:bg-emerald-600 dark:hover:bg-[#294D2D]",
    Icon: ThumbsUpIcon,
  },
  [AgenticScoreBucket.GOOD]: {
    label: "Good",
    className:
      "bg-emerald-100 text-emerald-700 dark:bg-[#383E33] dark:text-[#DCE1D8]",
    hoverClassName: "hover:bg-emerald-200 dark:hover:bg-[#505B49]",
    Icon: ThumbsUpIcon,
  },
  [AgenticScoreBucket.OKAY]: {
    label: "Okay",
    className:
      "bg-slate-100 text-slate-600 dark:bg-[#4C4F52] dark:text-[#EAEAEA]",
    hoverClassName: "hover:bg-slate-200 dark:hover:bg-[#77787A]",
    Icon: CircleMinusIcon,
  },
};

// Text tokens for the comms rail activity feed rows.
export const activityTypography = {
  body: "text-sm font-normal leading-normal text-slate-600",
  emphasis: "font-medium text-slate-800",
  meta: "text-xs font-normal text-slate-400",
  quote: "text-sm font-normal leading-relaxed text-slate-600",
};

// Underlined sub-tab triggers inside the candidacy comms rail.
export const commsTabTriggerClassName =
  "w-auto flex-none justify-start rounded-none border-0 border-b-2 border-transparent bg-transparent px-3 py-2.5 font-normal text-slate-500 shadow-none hover:text-slate-700 data-[state=active]:border-0 data-[state=active]:border-b-2 data-[state=active]:border-b-primary-900 data-[state=active]:bg-transparent data-[state=active]:font-medium data-[state=active]:text-primary-900 data-[state=active]:shadow-none dark:data-[state=active]:bg-transparent";

export const agenticScoreBadgeClassNames = {
  sm: "gap-1.5 px-3 py-1 text-sm",
  xs: "gap-1 px-2.5 py-0.5 text-[11px]",
};

// Label, icon, and icon-circle styling for the notification type filter dropdown + chips.
export const notificationCategoryToMeta: Record<
  NotificationCategoryId,
  { label: string; className: string; Icon: LucideIcon }
> = {
  [NotificationCategoryId.IMMEDIATE]: {
    label: "Immediate",
    className: "bg-teal-50 text-teal-600",
    Icon: MessageCircleWarningIcon,
  },
  [NotificationCategoryId.DAILY_BATCHED]: {
    label: "Daily / Batched",
    className: "bg-blue-50 text-blue-600",
    Icon: LayersIcon,
  },
  [NotificationCategoryId.TEAM]: {
    label: "Team",
    className: "bg-purple-50 text-purple-600",
    Icon: UsersIcon,
  },
};

// The specific notification types the inbox can be scoped to. Absent types are
// only filterable through their category.
export const notificationTypeFilterToMeta: Partial<
  Record<NotificationType, { label: string; Icon: LucideIcon }>
> = {
  [NotificationType.EXCELLENT_CANDIDATE]: {
    label: "Excellent candidates",
    Icon: StarIcon,
  },
  [NotificationType.INTERNAL_RECOMMENDATION]: {
    label: "Recommended candidates",
    Icon: StarIcon,
  },
  [NotificationType.INTERNAL_COMMENT_MENTION]: {
    label: "Comment mentions",
    Icon: AtSignIcon,
  },
  [NotificationType.THREAD_REPLY]: {
    label: "Thread replies",
    Icon: MessageSquareIcon,
  },
};

// Types with no entry are team slack channel only and never render in the app.
export const notificationTypeToCategoryId: Partial<
  Record<NotificationType, NotificationCategoryId>
> = {
  [NotificationType.EXCELLENT_CANDIDATE]: NotificationCategoryId.IMMEDIATE,
  [NotificationType.INTERNAL_RECOMMENDATION]: NotificationCategoryId.IMMEDIATE,
  [NotificationType.INTERNAL_COMMENT_MENTION]: NotificationCategoryId.IMMEDIATE,
  [NotificationType.THREAD_REPLY]: NotificationCategoryId.IMMEDIATE,
  [NotificationType.JOB_COMMENT_MENTION]: NotificationCategoryId.IMMEDIATE,
  [NotificationType.COMPANY_COMMENT_MENTION]: NotificationCategoryId.IMMEDIATE,
  [NotificationType.EXPIRING_CANDIDATES]: NotificationCategoryId.DAILY_BATCHED,
  [NotificationType.INTERVIEWS_TODAY]: NotificationCategoryId.DAILY_BATCHED,
  [NotificationType.PLACEMENT_CHECKINS]: NotificationCategoryId.DAILY_BATCHED,
  [NotificationType.MISSING_START_DATES]: NotificationCategoryId.DAILY_BATCHED,
  [NotificationType.LEADERBOARD_DAILY]: NotificationCategoryId.TEAM,
  [NotificationType.LEADERBOARD_WEEKLY]: NotificationCategoryId.TEAM,
  [NotificationType.SEARCH_LEADERBOARD_MONTHLY]: NotificationCategoryId.TEAM,
  [NotificationType.PERSONAL_RECORD]: NotificationCategoryId.IMMEDIATE,
  [NotificationType.MILESTONE_UNLOCKED]: NotificationCategoryId.IMMEDIATE,
  [NotificationType.PAUSED_JOBS_REMINDER]: NotificationCategoryId.DAILY_BATCHED,
  [NotificationType.COMMS_TO_SEND]: NotificationCategoryId.DAILY_BATCHED,
  [NotificationType.STALE_CANDIDACIES]: NotificationCategoryId.DAILY_BATCHED,
  [NotificationType.TEAM_WIDE_SUBMIT]: NotificationCategoryId.IMMEDIATE,
  [NotificationType.BATCH_SENT_FOLLOW_UP]: NotificationCategoryId.TEAM,
  [NotificationType.INTERVIEW_BLOCKS_FREED]: NotificationCategoryId.IMMEDIATE,
  [NotificationType.CANDIDATE_REQUESTED_CALL]: NotificationCategoryId.IMMEDIATE,
  [NotificationType.ACTIVE_CANDIDATE_FINISHED_STEVIE]:
    NotificationCategoryId.IMMEDIATE,
  [NotificationType.CANDIDATE_RESURRECTED_AFTER_STEVIE]:
    NotificationCategoryId.IMMEDIATE,
  [NotificationType.CANDIDACY_AUTO_SCREENED]: NotificationCategoryId.IMMEDIATE,
  [NotificationType.INCENTIVE_REDEEMED]: NotificationCategoryId.TEAM,
  [NotificationType.JOB_PAUSE_REQUEST_DECIDED]:
    NotificationCategoryId.IMMEDIATE,
};

// Mirrors guac's NOTIFICATION_CATALOG to set guard rails on notification settings.
const standardNotificationDefaults = {
  [NotificationChannel.IN_APP]: true,
  [NotificationChannel.EMAIL]: false,
  [NotificationChannel.SLACK]: false,
};
// Every mention type shares one entry so the grouped settings row has a single,
// unambiguous set of guard rails.
const mentionNotificationEntry: NotificationCatalogEntry = {
  category: NotificationCategoryId.IMMEDIATE,
  defaults: {
    [NotificationChannel.IN_APP]: true,
    [NotificationChannel.EMAIL]: false,
    [NotificationChannel.SLACK]: true,
  },
  requiresAtLeastOneChannel: true,
  requiredChannel: [NotificationChannel.IN_APP, NotificationChannel.SLACK],
  preferenceGroup: NotificationPreferenceGroup.MENTIONS,
};
// A high-scoring candidate and a teammate's recommendation are the same alert to
// a staffer, so they share one entry and move together in settings.
const topCandidateNotificationEntry: NotificationCatalogEntry = {
  category: NotificationCategoryId.IMMEDIATE,
  defaults: {
    [NotificationChannel.IN_APP]: true,
    [NotificationChannel.EMAIL]: false,
    [NotificationChannel.SLACK]: true,
  },
  requiresAtLeastOneChannel: true,
  requiredChannel: [NotificationChannel.IN_APP, NotificationChannel.SLACK],
  preferenceGroup: NotificationPreferenceGroup.TOP_CANDIDATES,
};
export const NOTIFICATION_CATALOG: Partial<
  Record<NotificationType, NotificationCatalogEntry>
> = {
  [NotificationType.EXCELLENT_CANDIDATE]: topCandidateNotificationEntry,
  [NotificationType.INTERNAL_RECOMMENDATION]: topCandidateNotificationEntry,
  [NotificationType.INTERNAL_COMMENT_MENTION]: mentionNotificationEntry,
  [NotificationType.THREAD_REPLY]: mentionNotificationEntry,
  [NotificationType.JOB_COMMENT_MENTION]: mentionNotificationEntry,
  [NotificationType.COMPANY_COMMENT_MENTION]: mentionNotificationEntry,
  [NotificationType.EXPIRING_CANDIDATES]: {
    category: NotificationCategoryId.DAILY_BATCHED,
    defaults: standardNotificationDefaults,
    requiresAtLeastOneChannel: false,
    requiredChannel: [],
  },
  [NotificationType.INTERVIEWS_TODAY]: {
    category: NotificationCategoryId.DAILY_BATCHED,
    defaults: standardNotificationDefaults,
    requiresAtLeastOneChannel: false,
    requiredChannel: [],
  },
  [NotificationType.PLACEMENT_CHECKINS]: {
    category: NotificationCategoryId.DAILY_BATCHED,
    defaults: standardNotificationDefaults,
    requiresAtLeastOneChannel: false,
    requiredChannel: [],
  },
  [NotificationType.MISSING_START_DATES]: {
    category: NotificationCategoryId.DAILY_BATCHED,
    defaults: standardNotificationDefaults,
    requiresAtLeastOneChannel: false,
    requiredChannel: [],
  },
  [NotificationType.LEADERBOARD_WEEKLY]: {
    category: NotificationCategoryId.TEAM,
    defaults: {
      [NotificationChannel.IN_APP]: false,
      [NotificationChannel.EMAIL]: false,
      [NotificationChannel.SLACK]: true,
    },
    requiresAtLeastOneChannel: true,
    requiredChannel: [NotificationChannel.SLACK],
  },
  [NotificationType.SEARCH_LEADERBOARD_MONTHLY]: {
    category: NotificationCategoryId.TEAM,
    defaults: {
      [NotificationChannel.IN_APP]: false,
      [NotificationChannel.EMAIL]: false,
      [NotificationChannel.SLACK]: true,
    },
    requiresAtLeastOneChannel: true,
    requiredChannel: [NotificationChannel.SLACK],
  },
  [NotificationType.PERSONAL_RECORD]: {
    category: NotificationCategoryId.IMMEDIATE,
    defaults: standardNotificationDefaults,
    requiresAtLeastOneChannel: false,
    requiredChannel: [],
  },
  [NotificationType.MILESTONE_UNLOCKED]: {
    category: NotificationCategoryId.IMMEDIATE,
    defaults: standardNotificationDefaults,
    requiresAtLeastOneChannel: false,
    requiredChannel: [],
  },
  [NotificationType.PAUSED_JOBS_REMINDER]: {
    category: NotificationCategoryId.DAILY_BATCHED,
    defaults: standardNotificationDefaults,
    requiresAtLeastOneChannel: false,
    requiredChannel: [],
  },
  [NotificationType.COMMS_TO_SEND]: {
    category: NotificationCategoryId.DAILY_BATCHED,
    defaults: standardNotificationDefaults,
    requiresAtLeastOneChannel: false,
    requiredChannel: [],
  },
  [NotificationType.STALE_CANDIDACIES]: {
    category: NotificationCategoryId.DAILY_BATCHED,
    defaults: standardNotificationDefaults,
    requiresAtLeastOneChannel: false,
    requiredChannel: [],
  },
  [NotificationType.TEAM_WIDE_SUBMIT]: {
    category: NotificationCategoryId.IMMEDIATE,
    defaults: {
      [NotificationChannel.IN_APP]: true,
      [NotificationChannel.EMAIL]: false,
      [NotificationChannel.SLACK]: false,
    },
    requiresAtLeastOneChannel: true,
    requiredChannel: [NotificationChannel.IN_APP],
    unavailableChannel: [NotificationChannel.EMAIL, NotificationChannel.SLACK],
  },
  [NotificationType.BATCH_SENT_FOLLOW_UP]: {
    category: NotificationCategoryId.TEAM,
    defaults: {
      [NotificationChannel.IN_APP]: false,
      [NotificationChannel.EMAIL]: false,
      [NotificationChannel.SLACK]: true,
    },
    requiresAtLeastOneChannel: true,
    requiredChannel: [NotificationChannel.SLACK],
  },
  [NotificationType.INTERVIEW_BLOCKS_FREED]: {
    category: NotificationCategoryId.IMMEDIATE,
    defaults: {
      [NotificationChannel.IN_APP]: true,
      [NotificationChannel.EMAIL]: false,
      [NotificationChannel.SLACK]: true,
    },
    requiresAtLeastOneChannel: true,
    requiredChannel: [NotificationChannel.SLACK],
  },
  [NotificationType.CANDIDATE_REQUESTED_CALL]: {
    category: NotificationCategoryId.IMMEDIATE,
    defaults: {
      [NotificationChannel.IN_APP]: true,
      [NotificationChannel.EMAIL]: false,
      [NotificationChannel.SLACK]: true,
    },
    requiresAtLeastOneChannel: true,
    requiredChannel: [NotificationChannel.IN_APP, NotificationChannel.SLACK],
    unavailableChannel: [NotificationChannel.EMAIL],
  },
  [NotificationType.ACTIVE_CANDIDATE_FINISHED_STEVIE]: {
    category: NotificationCategoryId.IMMEDIATE,
    defaults: {
      [NotificationChannel.IN_APP]: true,
      [NotificationChannel.EMAIL]: false,
      [NotificationChannel.SLACK]: false,
    },
    requiresAtLeastOneChannel: true,
    requiredChannel: [NotificationChannel.IN_APP],
  },
  [NotificationType.CANDIDATE_RESURRECTED_AFTER_STEVIE]: {
    category: NotificationCategoryId.IMMEDIATE,
    defaults: {
      [NotificationChannel.IN_APP]: true,
      [NotificationChannel.EMAIL]: false,
      [NotificationChannel.SLACK]: false,
    },
    requiresAtLeastOneChannel: true,
    requiredChannel: [NotificationChannel.IN_APP],
  },
  [NotificationType.CANDIDACY_AUTO_SCREENED]: {
    category: NotificationCategoryId.IMMEDIATE,
    defaults: standardNotificationDefaults,
    requiresAtLeastOneChannel: false,
    requiredChannel: [],
  },
  [NotificationType.INCENTIVE_REDEEMED]: {
    category: NotificationCategoryId.TEAM,
    defaults: {
      [NotificationChannel.IN_APP]: true,
      [NotificationChannel.EMAIL]: false,
      [NotificationChannel.SLACK]: false,
    },
    requiresAtLeastOneChannel: true,
    requiredChannel: [NotificationChannel.IN_APP],
    unavailableChannel: [NotificationChannel.EMAIL, NotificationChannel.SLACK],
  },
  [NotificationType.JOB_PAUSE_REQUEST_DECIDED]: {
    category: NotificationCategoryId.IMMEDIATE,
    defaults: {
      [NotificationChannel.IN_APP]: true,
      [NotificationChannel.EMAIL]: false,
      [NotificationChannel.SLACK]: false,
    },
    requiresAtLeastOneChannel: true,
    requiredChannel: [NotificationChannel.IN_APP],
    unavailableChannel: [NotificationChannel.EMAIL, NotificationChannel.SLACK],
  },
};

export const notificationChannelToLabel: Record<NotificationChannel, string> = {
  [NotificationChannel.IN_APP]: "In App",
  [NotificationChannel.EMAIL]: "Email",
  [NotificationChannel.SLACK]: "Slack",
};

// Notifications settings page labels
export const notificationTypeToLabel: Partial<
  Record<NotificationType, string>
> = {
  [NotificationType.EXPIRING_CANDIDATES]:
    "These candidates are about to expire (could not contact)",
  [NotificationType.INTERVIEWS_TODAY]: "You have interviews scheduled today",
  [NotificationType.PLACEMENT_CHECKINS]:
    "Placement check-ins (30/60/90-day) are due this week",
  [NotificationType.MISSING_START_DATES]: "Placements are missing a start date",
  [NotificationType.LEADERBOARD_DAILY]: "Daily submit leaderboard recap",
  [NotificationType.LEADERBOARD_WEEKLY]: "End-of-week leaderboard winners",
  [NotificationType.SEARCH_LEADERBOARD_MONTHLY]:
    "Monthly LaborupSearch leaderboard",
  [NotificationType.PERSONAL_RECORD]: "You set a new personal record",
  [NotificationType.MILESTONE_UNLOCKED]: "You unlocked a milestone",
  [NotificationType.PAUSED_JOBS_REMINDER]: "Jobs have been paused for a while",
  [NotificationType.COMMS_TO_SEND]:
    "Candidates are overdue for a comm from you",
  [NotificationType.STALE_CANDIDACIES]:
    "Candidacies in your pipeline have gone stale",
  [NotificationType.TEAM_WIDE_SUBMIT]: "A teammate submits candidates to a job",
  [NotificationType.BATCH_SENT_FOLLOW_UP]:
    "Candidates you sent are still sitting in Batch Sent",
  [NotificationType.INTERVIEW_BLOCKS_FREED]:
    "Customer interview blocks on your calendar were opened up for screens",
  [NotificationType.CANDIDATE_REQUESTED_CALL]:
    "A candidate asked the scheduling agent to call them right now",
  [NotificationType.ACTIVE_CANDIDATE_FINISHED_STEVIE]:
    "A candidate you're working finished a Stevie call",
  [NotificationType.CANDIDATE_RESURRECTED_AFTER_STEVIE]:
    "A candidate you rejected was resurrected by their Stevie call",
  [NotificationType.CANDIDACY_AUTO_SCREENED]:
    "A candidacy was auto-moved to Screened after a 2+ minute call",
  [NotificationType.INCENTIVE_REDEEMED]:
    "A teammate cashes in an incentive token",
  [NotificationType.JOB_PAUSE_REQUEST_DECIDED]:
    "Your request to pause or cover a job was decided",
};

// Grouped types render as one settings row under this label instead of one row
// per type.
export const notificationPreferenceGroupToLabel: Record<
  NotificationPreferenceGroup,
  string
> = {
  [NotificationPreferenceGroup.MENTIONS]:
    "Someone mentions you or replies to a thread you're in",
  [NotificationPreferenceGroup.TOP_CANDIDATES]:
    "A top candidate enters your pipeline or a teammate recommends one",
};

const notificationCatalogTypes = Object.keys(
  NOTIFICATION_CATALOG,
) as NotificationType[];

// Derived from the catalog so a group and its members can never disagree.
export const notificationPreferenceGroupToTypes: Record<
  NotificationPreferenceGroup,
  NotificationType[]
> = {
  [NotificationPreferenceGroup.MENTIONS]: notificationCatalogTypes.filter(
    (type) =>
      NOTIFICATION_CATALOG[type]?.preferenceGroup ===
      NotificationPreferenceGroup.MENTIONS,
  ),
  [NotificationPreferenceGroup.TOP_CANDIDATES]: notificationCatalogTypes.filter(
    (type) =>
      NOTIFICATION_CATALOG[type]?.preferenceGroup ===
      NotificationPreferenceGroup.TOP_CANDIDATES,
  ),
};

export const notificationCategoryToDescription: Record<
  NotificationCategoryId,
  string
> = {
  [NotificationCategoryId.IMMEDIATE]:
    "Real-time alerts that need your attention right away.",
  [NotificationCategoryId.DAILY_BATCHED]:
    "Summarized updates delivered once per day.",
  [NotificationCategoryId.TEAM]: "Updates about your team and shared goals.",
};

// The mobile push settings section, which sits under Immediate. Push settings
// are per type and pick a loudness rather than channels.
export const mobilePushSectionMeta = {
  label: "Staffer Mobile Push Notifications",
  description:
    "Pushes to the Staffer app on your phone. Quiet ones skip the sound and banner and wait in Notification Center.",
  className: "bg-amber-50 text-amber-600",
  Icon: SmartphoneIcon,
};

// Rows in the mobile push section, in display order. Push types not listed are
// still settings in guac, just not ones staffers can change.
export const PUSH_TYPES_IN_ORDER: PushType[] = [
  PushType.EXCELLENT_CANDIDATE,
  PushType.APPLICATION,
];

export const pushTypeToSettingsMeta: Partial<
  Record<
    PushType,
    {
      label: string;
      // Locked to its current mode — guac rejects changes too
      isRequired?: boolean;
      // Shown as a badge next to the label
      note?: string;
      // The options offered; every mode when absent. Guac enforces it too.
      modes?: PushMode[];
    }
  >
> = {
  [PushType.EXCELLENT_CANDIDATE]: {
    label: "Excellent candidates",
    isRequired: true,
    note: "Only sends if offline for 5+ min",
  },
  [PushType.APPLICATION]: {
    label: "Applications on your jobs",
    note: "Pauses at 20+ in Applied stage",
    modes: [PushMode.QUIET, PushMode.OFF],
  },
};

export const PUSH_MODES_IN_ORDER: PushMode[] = [
  PushMode.ALERT,
  PushMode.QUIET,
  PushMode.OFF,
];

export const pushModeToLabel: Record<PushMode, string> = {
  [PushMode.ALERT]: "Alert",
  [PushMode.QUIET]: "Quiet",
  [PushMode.OFF]: "Off",
};

export const defaultNotificationFilterState: NotificationFilterState = {
  categoryIds: [],
  inboxFilter: NotificationInboxFilter.ALL,
  searchQuery: "",
  types: [],
};

export const agenticScoreIconClassNames = {
  sm: "h-4 w-4",
  xs: "h-3 w-3",
};

export const agenticScoreOverrideBadgeClassNames = {
  sm: "-right-1.5 -top-1.5 h-5 w-5",
  xs: "-right-1.5 -top-1.5 h-3.5 w-3.5",
};

export const agenticScoreOverrideIconClassNames = {
  sm: "h-3 w-3",
  xs: "h-2 w-2",
};

// Custom icon if one doesn't have a conversation id with a candidacy
export const ClipboardArrowUpRight = createLucideIcon("ClipboardArrowUpRight", [
  ["rect", { x: "8", y: "2", width: "8", height: "4", rx: "1", key: "clip" }],
  ["path", { d: "M16 4h2a2 2 0 0 1 2 2v6", key: "right" }],
  ["path", { d: "M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8", key: "frame" }],
  ["path", { d: "M14 18l6-6", key: "shaft" }],
  ["path", { d: "M15 12h5v5", key: "head" }],
]);

// QUERY KEYS //

// Shared by the popup stack and the sidebar unread count so they poll in
// lockstep — otherwise the badge count can lag behind what's popping up.
export const NOTIFICATIONS_POLL_INTERVAL_MS = 60_000;

export const popupNotificationsQueryKey = ["notifications", "popup"];

export const unreadNotificationsCountQueryKey = [
  "notifications",
  "unread-count",
];

export const userNotificationPreferencesQueryKey = [
  "user-notification-preferences",
];

export const userPushPreferencesQueryKey = ["user-push-preferences"];

export const pinnedJobsQueryKey = ["pinned-jobs"] as const;

// Base key so note/evaluation mutations can invalidate every sheet's counts.
export const candidateSheetV2TabCountsQueryKey = ["v2-tab-counts"] as const;

export const petYardWeatherConditionToLabel: Record<
  PetYardWeatherCondition,
  string
> = {
  [PetYardWeatherCondition.CLEAR]: "Clear",
  [PetYardWeatherCondition.PARTLY_CLOUDY]: "Partly cloudy",
  [PetYardWeatherCondition.OVERCAST]: "Overcast",
  [PetYardWeatherCondition.FOG]: "Fog",
  [PetYardWeatherCondition.DRIZZLE]: "Drizzle",
  [PetYardWeatherCondition.RAIN]: "Rain",
  [PetYardWeatherCondition.SNOW]: "Snow",
  [PetYardWeatherCondition.THUNDERSTORM]: "Thunderstorm",
};

export const playyardSkyPhaseToLabel: Record<PlayyardSkyPhase, string> = {
  [PlayyardSkyPhase.DAWN]: "Dawn",
  [PlayyardSkyPhase.DAY]: "Day",
  [PlayyardSkyPhase.GOLDEN_HOUR]: "Golden hour",
  [PlayyardSkyPhase.BLUE_HOUR]: "Blue hour",
  [PlayyardSkyPhase.NIGHT]: "Night",
};

export const STAFFER_PET_SOURCE_SIZE_PX = 256;

// Winterfell keeps the live solar clock without local weather effects.
export const IS_PLAYYARD_WEATHER_ENABLED = false;

export const EMPTY_PLAYYARD_SKY_PREVIEW: PlayyardSkyPreview = {
  phase: null,
  condition: null,
  isWindy: false,
  isFreezing: false,
};

// Standing water in the playyard, as % of the scene: the pond (matches
// PlayyardPond's box) plus rain puddles. Rain ripples land on these.
export const PLAYYARD_WATER_SURFACES = {
  pond: { centerX: 17.5, centerY: 65, radiusX: 13.5, radiusY: 6 },
  puddles: [
    { centerX: 52, centerY: 90, radiusX: 4, radiusY: 1.3 },
    { centerX: 79, centerY: 72, radiusX: 3, radiusY: 1 },
    { centerX: 36, centerY: 83, radiusX: 3.4, radiusY: 1.1 },
  ],
};

export const sadieDashboardRangeToLabel: Record<SadieDashboardRange, string> = {
  [SadieDashboardRange.LAST_7_DAYS]: "Last 7 days",
  [SadieDashboardRange.LAST_30_DAYS]: "Last 30 days",
  [SadieDashboardRange.LAST_90_DAYS]: "Last 90 days",
};

export const sadieDashboardRangeToDays: Record<SadieDashboardRange, number> = {
  [SadieDashboardRange.LAST_7_DAYS]: 7,
  [SadieDashboardRange.LAST_30_DAYS]: 30,
  [SadieDashboardRange.LAST_90_DAYS]: 90,
};
